import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase/config';
import { collection, getDocs, doc, updateDoc, setDoc, deleteDoc, query, where, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Engagements from './Engagements';
import AdminWorkbookViewer from './AdminWorkbookViewer';
import ResourcesHub from './ResourcesHub';
import PromptLibrary from './PromptLibrary';
import References from './References';
import ManageVideos from './ManageVideos';
import ManageReferences from './ManageReferences';
import ManageResourceTemplates from './ManageResourceTemplates';
import AgentLibrary from './AgentLibrary';
import InviteParticipants from './InviteParticipants';
import EmailTemplates from './EmailTemplates';
import UseCaseFinder from './UseCaseFinder';

const LEVELS = [
  { min: 4, max: 7, label: 'AI Newcomer', cls: 'level-1' },
  { min: 8, max: 11, label: 'Early Explorer', cls: 'level-2' },
  { min: 12, max: 15, label: 'Developing Practitioner', cls: 'level-3' },
  { min: 16, max: 18, label: 'Active Practitioner', cls: 'level-4' },
  { min: 19, max: 20, label: 'AI Champion', cls: 'level-5' },
];

const getLevel = (score) => LEVELS.find(l => score >= l.min && score <= l.max) || LEVELS[0];

const levelColors = {
  'level-1': { background: '#fdecea', color: '#a32d2d' },
  'level-2': { background: '#fef5e4', color: '#854f0b' },
  'level-3': { background: '#eaf3de', color: '#3b6d11' },
  'level-4': { background: '#e1f5ee', color: '#0f6e56' },
  'level-5': { background: '#e6f1fb', color: '#185fa5' },
};

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const WORKBOOK_SECTION_LABELS = {
  day1: ['RICE Prompt — Role', 'RICE Prompt — Intent', 'RICE Prompt — Context', 'RICE Prompt — Extras', 'What came back', 'What would you change', 'Revised prompt', 'Reflection — Question 1', 'Reflection — Question 2', 'Before Day 2 task'],
  day2: ['Chain task', 'What first prompt produced', 'Chain prompt', 'Chain result', 'Iteration task', 'What was off', 'Iteration prompt', 'What changed', 'AI Chain Score', 'AI Iteration Score', 'Reflection — Question 1', 'Reflection — Question 2'],
};

// Workbook answers are not always plain strings: some days store numbers (time
// audits) and structured lists (workflow steps). These helpers render any shape
// without crashing the page.
const hasContent = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number' || typeof v === 'boolean') return true;
  if (Array.isArray(v)) return v.some(hasContent);
  if (typeof v === 'object') return Object.values(v).some(hasContent);
  return false;
};

const prettyKey = (k) => String(k).replace(/_/g, ' ');

const renderAnswer = (v, depth = 0) => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    return v.filter(hasContent).map((item, i) => (
      <div key={i} style={{ padding: '6px 0', borderTop: i ? '1px solid #F2F0EC' : 'none' }}>
        {renderAnswer(item, depth + 1)}
      </div>
    ));
  }
  if (typeof v === 'object') {
    return Object.entries(v).filter(([, x]) => hasContent(x)).map(([k, x]) => (
      <div key={k} style={{ marginBottom: '4px' }}>
        <span style={{ fontSize: '11px', fontWeight: '700', color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{prettyKey(k)}: </span>
        <span>{renderAnswer(x, depth + 1)}</span>
      </div>
    ));
  }
  return null;
};

export default function AdminDashboard({ user }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ company: '', department: '', role: '', level: '', name: '' });
  const [sort, setSort] = useState({ key: '', dir: 1 });
  const [resetBusy, setResetBusy] = useState({});
  const [tempPwResult, setTempPwResult] = useState(null);
  const [tempPwCopied, setTempPwCopied] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const [resendingUid, setResendingUid] = useState(null);
  const [journeyBase, setJourneyBase] = useState('registered');
  const [inviteStats, setInviteStats] = useState({ invites: [], codeToCompany: {} });

  // Invite records + code-to-company map for the invited-vs-joined stat.
  useEffect(() => {
    const loadInvites = async () => {
      try {
        const [invSnap, engSnap] = await Promise.all([
          getDocs(collection(db, 'invites')),
          getDocs(collection(db, 'engagements')),
        ]);
        const codeToCompany = {};
        engSnap.docs.forEach(d => { codeToCompany[d.id] = d.data().company || ''; });
        setInviteStats({ invites: invSnap.docs.map(d => d.data()), codeToCompany });
      } catch { /* invite stats are optional; dashboard still works */ }
    };
    loadInvites();
  }, []);
  const [adminPage, setAdminPage] = useState('participants');
  const [selected, setSelected] = useState({});
  const [bulkAction, setBulkAction] = useState({ dayRangeStart: '', dayRangeEnd: '' });
  const [bulkVideoDays, setBulkVideoDays] = useState([]);
  const [actionMsg, setActionMsg] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [emailEdit, setEmailEdit] = useState(null);
  const [emailEditValue, setEmailEditValue] = useState('');
  const [emailEditSaving, setEmailEditSaving] = useState(false);
  const [detailsEdit, setDetailsEdit] = useState(null);
  const [detailsEditValue, setDetailsEditValue] = useState({ department: '', jobTitle: '' });
  const [detailsEditSaving, setDetailsEditSaving] = useState(false);
  const [participantDetail, setParticipantDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailDay, setDetailDay] = useState(1);
  const [adminMsg, setAdminMsg] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [usersSnap, assessSnap, engSnap] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'assessments')),
      getDocs(collection(db, 'engagements')),
    ]);
    const assessMap = {};
    assessSnap.forEach(d => { assessMap[d.id] = d.data(); });
    const engMap = {};
    engSnap.forEach(d => { engMap[d.id] = d.data(); });
    const all = [];
    usersSnap.forEach(d => {
      const u = d.data();
      if (['tcfadmin'].includes(u.adminRole)) return;
      const assess = assessMap[d.id] || {};
      const code = u.companyCode || u.engagementId || '';
      const eng = engMap[code] || {};
      all.push({
        id: d.id,
        name: u.displayName || u.email || 'Unknown',
        email: u.email || '',
        company: u.company || '—',
        companyCode: code,
        department: u.department || '—',
        role: u.jobTitle || u.role || '—',
        preScore: assess.pre?.scores?.total || null,
        postScore: assess.post?.scores?.total || null,
        preLevel: assess.pre?.scores ? getLevel(assess.pre.scores.total).label : '—',
        postLevel: assess.post?.scores ? getLevel(assess.post.scores.total).label : '—',
        preScores: assess.pre?.scores || null,
        postScores: assess.post?.scores || null,
        dayRangeStart: u.dayRangeStart || null,
        dayRangeEnd: u.dayRangeEnd || null,
        postAssessmentUnlocked: u.postAssessmentUnlocked || false,
        accessActive: u.accessActive !== false,
        adminRole: u.adminRole || 'participant',
        videoAccess: Array.isArray(u.videoAccess) ? u.videoAccess : [],
        cohortVideosEnabled: !!eng.videosEnabled,
      });
    });
    setParticipants(all);
    setLoading(false);

    // Load workbook progress counts in the background so the table appears
    // immediately and the Workbooks column fills in a moment later. Reads
    // only the per-user summary doc, never the day content docs.
    try {
      const chunkSize = 25;
      const withProgress = [...all];
      for (let i = 0; i < withProgress.length; i += chunkSize) {
        const slice = withProgress.slice(i, i + chunkSize);
        const [snaps, exSnaps] = await Promise.all([
          Promise.all(slice.map(p => getDoc(doc(db, 'workbooks', p.id)).catch(() => null))),
          Promise.all(slice.map(p => getDoc(doc(db, 'examResults', p.id)).catch(() => null))),
        ]);
        snaps.forEach((snap, j) => {
          const p = slice[j];
          let completedDays = 0;
          let sectionsCompleted = 0;
          if (snap && snap.exists()) {
            const progress = snap.data().progress || {};
            const dayStart = p.dayRangeStart || 1;
            const dayEnd = p.dayRangeEnd || 10;
            for (let d = 1; d <= 10; d++) {
              const entry = progress[`day${d}`];
              if (!entry) continue;
              const data = entry.sections || entry.fields || {};
              Object.values(data).forEach(v => {
                if (v !== null && v !== undefined && String(v).trim() !== '') sectionsCompleted++;
              });
              if (entry.status === 'complete' && d >= dayStart && d <= dayEnd) completedDays++;
            }
            p.assignedDays = Math.max(0, (p.dayRangeEnd || 10) - (p.dayRangeStart || 1) + 1);
          } else {
            p.assignedDays = Math.max(0, (p.dayRangeEnd || 10) - (p.dayRangeStart || 1) + 1);
          }
          p.completedDays = completedDays;
          p.sectionsCompleted = sectionsCompleted;
          const exSnap = exSnaps[j];
          p.examTaken = !!(exSnap && exSnap.exists());
          p.examPassed = !!(exSnap && exSnap.exists() && exSnap.data().passed);
          p.examScore = exSnap && exSnap.exists() ? (exSnap.data().totalScore ?? null) : null;
        });
      }
      setParticipants([...withProgress]);
    } catch (err) {
      console.error('Workbook progress load failed:', err);
    }
  };

  const loadParticipantDetail = async (p) => {
    setLoadingDetail(true);
    setSelectedParticipant(p);
    setDetailDay(1);
    const [wbSnap, assessSnap] = await Promise.all([
      getDoc(doc(db, 'workbooks', p.id)),
      getDoc(doc(db, 'assessments', p.id)),
    ]);
    const wbData = wbSnap.exists() ? wbSnap.data() : null;
    const assessData = assessSnap.exists() ? assessSnap.data() : null;
    setParticipantDetail({ workbook: wbData, assessment: assessData });
    setLoadingDetail(false);
  };

  const makeAdmin = async (p) => {
    await setDoc(doc(db, 'users', p.id), {
      adminRole: 'companyadmin',
      adminCompanyCode: p.companyCode,
      adminCompany: p.company,
    }, { merge: true });
    // add to engagement admins list
    if (p.companyCode) {
      const engSnap = await getDoc(doc(db, 'engagements', p.companyCode));
      if (engSnap.exists()) {
        const current = engSnap.data().admins || [];
        if (!current.includes(p.email)) {
          await updateDoc(doc(db, 'engagements', p.companyCode), { admins: [...current, p.email] });
        }
      }
    }
    // Grant server-side company-admin permission (the source of truth that the
    // security rules and Cloud Functions actually check). Union the code so we
    // never clobber an admin who already manages several codes.
    if (p.email && p.companyCode) {
      const caRef = doc(db, 'companyAdmins', p.email);
      const caSnap = await getDoc(caRef);
      const existingCodes = caSnap.exists() ? (caSnap.data().companyCodes || []) : [];
      const codes = Array.from(new Set([...existingCodes, p.companyCode]));
      await setDoc(caRef, { companyCodes: codes, company: p.company, email: p.email }, { merge: true });
    }
    setParticipants(prev => prev.map(x => x.id === p.id ? { ...x, adminRole: 'companyadmin' } : x));
    setAdminMsg({ type: 'success', text: `${p.name} is now a company admin for ${p.company}.` });
    setTimeout(() => setAdminMsg(null), 4000);
  };

  const removeAdmin = async (p) => {
    await setDoc(doc(db, 'users', p.id), { adminRole: 'participant', adminCompanyCode: null, adminCompany: null }, { merge: true });
    if (p.companyCode) {
      const engSnap = await getDoc(doc(db, 'engagements', p.companyCode));
      if (engSnap.exists()) {
        const updated = (engSnap.data().admins || []).filter(e => e !== p.email);
        await updateDoc(doc(db, 'engagements', p.companyCode), { admins: updated });
      }
    }
    // Revoke server-side permission for this code; remove the record entirely
    // if they no longer administer any codes.
    if (p.email) {
      const caRef = doc(db, 'companyAdmins', p.email);
      const caSnap = await getDoc(caRef);
      if (caSnap.exists()) {
        const remaining = (caSnap.data().companyCodes || []).filter(c => c !== p.companyCode);
        if (remaining.length) {
          await setDoc(caRef, { companyCodes: remaining }, { merge: true });
        } else {
          await deleteDoc(caRef);
        }
      }
    }
    setParticipants(prev => prev.map(x => x.id === p.id ? { ...x, adminRole: 'participant' } : x));
    setAdminMsg({ type: 'success', text: `${p.name} has been removed as company admin.` });
    setTimeout(() => setAdminMsg(null), 4000);
  };

  // Last resort when reset mail is blocked or pre-clicked by mail security:
  // set a password directly and read it to the person.
  const handleTempPassword = async (p) => {
    if (!window.confirm(`Set a temporary password for ${p.name || p.email}?\n\nThis replaces their current password immediately. You will read the new one to them directly, and the portal will ask them to change it after they sign in.`)) return;
    setResetBusy(prev => ({ ...prev, [p.id]: true }));
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'adminSetTempPassword');
      const res = await fn({ targetUid: p.id });
      setTempPwResult({ ...res.data, name: p.name || res.data.name, email: p.email || res.data.email });
    } catch (err) {
      setAdminMsg({ type: 'error', text: err.message || 'Could not set a temporary password.' });
      setTimeout(() => setAdminMsg(null), 6000);
    }
    setResetBusy(prev => ({ ...prev, [p.id]: false }));
  };

  // Send a branded reset email on someone's behalf, for the "I never got it" call.
  const handleSendReset = async (p) => {
    if (!p.email) return;
    if (!window.confirm(`Send a password reset email to ${p.email}?`)) return;
    setResetBusy(prev => ({ ...prev, [p.id]: true }));
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendPasswordReset');
      const res = await fn({ email: p.email });
      const d = res.data || {};
      setAdminMsg({ type: d.found === false ? 'error' : 'success', text: d.message || `Reset email sent to ${p.email}.` });
    } catch (err) {
      setAdminMsg({ type: 'error', text: err.message || 'Could not send the reset email.' });
    }
    setResetBusy(prev => ({ ...prev, [p.id]: false }));
    setTimeout(() => setAdminMsg(null), 6000);
  };

  const openEmailEdit = (p) => { setEmailEdit(p); setEmailEditValue(p.email || ''); };

  const handleSaveEmail = async () => {
    const newEmail = emailEditValue.trim().toLowerCase();
    if (!newEmail) { setAdminMsg({ type: 'error', text: 'Enter an email address.' }); return; }
    setEmailEditSaving(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'adminUpdateUserEmail');
      await fn({ targetUid: emailEdit.id, newEmail });
      setParticipants(prev => prev.map(p => p.id === emailEdit.id ? { ...p, email: newEmail } : p));
      setAdminMsg({ type: 'success', text: `Email updated to ${newEmail}.` });
      setTimeout(() => setAdminMsg(null), 4000);
      setEmailEdit(null);
    } catch (err) {
      setAdminMsg({ type: 'error', text: err.message || 'Could not update the email.' });
    }
    setEmailEditSaving(false);
  };

  const openDetailsEdit = (p) => {
    setDetailsEdit(p);
    setDetailsEditValue({
      department: p.department === '—' ? '' : (p.department || ''),
      jobTitle: p.role === '—' ? '' : (p.role || ''),
    });
  };

  const handleSaveDetails = async () => {
    const department = detailsEditValue.department.trim();
    const jobTitle = detailsEditValue.jobTitle.trim();
    setDetailsEditSaving(true);
    try {
      // The roster maps jobTitle onto the local `role` key, so mirror that here.
      await updateDoc(doc(db, 'users', detailsEdit.id), { department, jobTitle });
      setParticipants(prev => prev.map(p => p.id === detailsEdit.id
        ? { ...p, department: department || '—', role: jobTitle || '—' }
        : p));
      setAdminMsg({ type: 'success', text: 'Department and job title updated.' });
      setTimeout(() => setAdminMsg(null), 4000);
      setDetailsEdit(null);
    } catch (err) {
      setAdminMsg({ type: 'error', text: err.message || 'Could not save the changes.' });
    }
    setDetailsEditSaving(false);
  };

  const handleSendReminders = async (targets) => {
    if (!targets.length) { setAdminMsg({ type: 'error', text: 'Select at least one participant.' }); return; }
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendReminders');
      const res = await fn({ uids: targets.map(p => p.id) });
      const d = res.data || {};
      setAdminMsg({ type: d.failedCount ? 'error' : 'success', text: `Reminder sent to ${d.sentCount}.${d.failedCount ? ` ${d.failedCount} failed.` : ''}` });
      setTimeout(() => setAdminMsg(null), 4000);
      setSelected({});
    } catch (err) {
      setAdminMsg({ type: 'error', text: err.message || 'Could not send reminders.' });
    }
  };

  // Sends one person their certificate email again, for when they say it
  // never arrived.
  const handleResendPassEmail = async (p) => {
    if (!window.confirm(`Send the certificate email to ${p.name || p.email} again?`)) return;
    setResendingUid(p.id);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'resendPassEmail');
      const res = await fn({ uid: p.id });
      setAdminMsg({ type: 'success', text: `Certificate email sent to ${(res.data || {}).email || p.email}.` });
      setTimeout(() => setAdminMsg(null), 4000);
    } catch (err) {
      setAdminMsg({ type: 'error', text: err.message || 'Couldn’t send that email.' });
    }
    setResendingUid(null);
  };

  // Sends the pass emails now instead of waiting for the 4pm run. Catch-up
  // mode includes everyone who has ever passed and has not been emailed yet.
  const handleRunDigest = async (includeAll) => {
    setDigestBusy(true);
    setDigestResult(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendExamPassDigestNow');
      const res = await fn({ includeAll });
      setDigestResult(res.data || {});
    } catch (err) {
      setDigestResult({ error: err.message || 'Couldn’t send the emails.' });
    }
    setDigestBusy(false);
  };

  const filtered = participants.filter(p => {
    if (filters.name && !p.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
    if (filters.company && p.company !== filters.company) return false;
    if (filters.department && p.department !== filters.department) return false;
    if (filters.role && p.role !== filters.role) return false;
    if (filters.level && p.preLevel !== filters.level && p.postLevel !== filters.level) return false;
    return true;
  });

  // Column sorting for the participants table. Empty values always sort last.
  const SORT_GETTERS = {
    name: p => (p.name || '').toLowerCase(),
    company: p => (p.company || '').toLowerCase(),
    department: p => (p.department || '').toLowerCase(),
    role: p => (p.role || '').toLowerCase(),
    preScore: p => p.preScore ?? null,
    postScore: p => p.postScore ?? null,
    change: p => (p.preScore && p.postScore) ? p.postScore - p.preScore : null,
    level: p => p.postScore ?? p.preScore ?? null,
    // Passed sorts above in-progress, which sorts above not started.
    exam: p => (p.examPassed ? 2000 : p.examTaken ? 1000 : null) + (p.examScore || 0),
    workbooks: p => p.completedDays ?? null,
    days: p => p.dayRangeStart ?? null,
    videos: p => Array.isArray(p.videoAccess) ? p.videoAccess.length : 99,
    postAssess: p => p.postAssessmentUnlocked ? 1 : 0,
    access: p => p.accessActive ? 1 : 0,
    adminRole: p => p.adminRole === 'companyadmin' ? 1 : 0,
  };
  const sorted = [...filtered];
  if (sort.key && SORT_GETTERS[sort.key]) {
    const get = SORT_GETTERS[sort.key];
    sorted.sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va < vb) return -1 * sort.dir;
      if (va > vb) return 1 * sort.dir;
      return 0;
    });
  }

  // The stat cards follow the company filter: pick a company and every card
  // reports on that company alone; leave it on All Companies for the totals.
  const companyScoped = filters.company ? participants.filter(p => p.company === filters.company) : participants;

  // Invited vs joined, scoped the same way. Joined only counts accounts
  // within the same company, so it can never exceed registered.
  const participantEmails = new Set(companyScoped.map(p => (p.email || '').toLowerCase()).filter(Boolean));
  const relevantInvites = filters.company
    ? inviteStats.invites.filter(inv => inviteStats.codeToCompany[inv.companyCode] === filters.company)
    : inviteStats.invites;
  // Count distinct people, not invite rows: the same person invited under two
  // codes is still one person.
  const invitedEmails = new Set(relevantInvites.map(inv => (inv.email || '').toLowerCase()).filter(Boolean));
  const invitedCount = invitedEmails.size;
  const joinedCount = [...invitedEmails].filter(e => participantEmails.has(e)).length;

  const companies = [...new Set(participants.map(p => p.company).filter(c => c !== '—'))];
  const departments = [...new Set(participants.map(p => p.department).filter(d => d !== '—'))];
  const roles = [...new Set(participants.map(p => p.role).filter(r => r !== '—'))];
  const avgPre = companyScoped.filter(p => p.preScore).reduce((a, p) => a + p.preScore, 0) / (companyScoped.filter(p => p.preScore).length || 1);
  const avgPost = companyScoped.filter(p => p.postScore).reduce((a, p) => a + p.postScore, 0) / (companyScoped.filter(p => p.postScore).length || 1);
  const bothCompleted = companyScoped.filter(p => p.preScore && p.postScore).length;

  // "Where everyone is" journey snapshot — follows the filters/search below.
  const journeyTotal = (journeyBase === 'invited' && invitedCount > 0 ? invitedCount : filtered.length) || 1;
  const journey = [
    ...(invitedCount > 0 ? [{ label: 'Invited', count: invitedCount, gold: false }] : []),
    { label: 'Registered', count: filtered.length, gold: false },
    { label: 'Snapshot taken', count: filtered.filter(p => p.preScore != null).length, gold: false },
    { label: 'Workbooks in progress', count: filtered.filter(p => (p.sectionsCompleted > 0 || p.completedDays > 0) && p.completedDays < (p.assignedDays ?? 10)).length, gold: false },
    { label: 'Workbooks complete', count: filtered.filter(p => (p.assignedDays ?? 10) > 0 && p.completedDays >= (p.assignedDays ?? 10)).length, gold: false },
    { label: 'Final exam in progress', count: filtered.filter(p => p.examTaken && !p.examPassed).length, gold: false },
    { label: 'Certified', count: filtered.filter(p => p.examPassed).length, gold: true },
  ];
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const getTargets = () => Object.keys(selected).filter(id => selected[id]).map(id => participants.find(p => p.id === id)).filter(Boolean);
  const toggleSelect = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleSelectAll = () => {
    const allSel = filtered.every(p => selected[p.id]);
    const newSel = {};
    filtered.forEach(p => { newSel[p.id] = !allSel; });
    setSelected(newSel);
  };

  const applyDayRange = async () => {
    const targets = getTargets();
    if (!targets.length) { setActionMsg({ type: 'error', text: 'Select at least one participant.' }); return; }
    if (!bulkAction.dayRangeStart || !bulkAction.dayRangeEnd) { setActionMsg({ type: 'error', text: 'Set both a start and end day.' }); return; }
    await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), { dayRangeStart: parseInt(bulkAction.dayRangeStart), dayRangeEnd: parseInt(bulkAction.dayRangeEnd) })));
    setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, dayRangeStart: parseInt(bulkAction.dayRangeStart), dayRangeEnd: parseInt(bulkAction.dayRangeEnd) } : p));
    setActionMsg({ type: 'success', text: `Day range updated for ${targets.length} participant${targets.length !== 1 ? 's' : ''}.` });
    setSelected({});
  };

  const applyPostUnlock = async (unlock) => {
    const targets = getTargets();
    if (!targets.length) { setActionMsg({ type: 'error', text: 'Select at least one participant.' }); return; }
    await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), { postAssessmentUnlocked: unlock })));
    setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, postAssessmentUnlocked: unlock } : p));
    setActionMsg({ type: 'success', text: `Post assessment ${unlock ? 'unlocked' : 'locked'} for ${targets.length} participant${targets.length !== 1 ? 's' : ''}.` });
    setSelected({});
  };

  const applyAccess = async (active) => {
    const targets = getTargets();
    if (!targets.length) { setActionMsg({ type: 'error', text: 'Select at least one participant.' }); return; }
    await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), { accessActive: active })));
    setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, accessActive: active } : p));
    setActionMsg({ type: 'success', text: `Access ${active ? 'restored' : 'ended'} for ${targets.length} participant${targets.length !== 1 ? 's' : ''}.` });
    setSelected({});
  };

  const toggleVideoDayChip = (day) => {
    setBulkVideoDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b));
  };

  const applyVideoAccess = async () => {
    const targets = getTargets();
    if (!targets.length) { setActionMsg({ type: 'error', text: 'Select at least one participant.' }); return; }
    if (!bulkVideoDays.length) { setActionMsg({ type: 'error', text: 'Pick at least one day to grant.' }); return; }
    await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), { videoAccess: bulkVideoDays })));
    setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, videoAccess: bulkVideoDays } : p));
    setActionMsg({ type: 'success', text: `Video access granted for Day ${bulkVideoDays.join(', Day ')} to ${targets.length} participant${targets.length !== 1 ? 's' : ''}.` });
    setSelected({});
    setBulkVideoDays([]);
  };

  const clearVideoAccess = async () => {
    const targets = getTargets();
    if (!targets.length) { setActionMsg({ type: 'error', text: 'Select at least one participant.' }); return; }
    await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), { videoAccess: [] })));
    setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, videoAccess: [] } : p));
    setActionMsg({ type: 'success', text: `Video access cleared for ${targets.length} participant${targets.length !== 1 ? 's' : ''}.` });
    setSelected({});
    setBulkVideoDays([]);
  };

  const openDeleteModal = (targets) => {
    if (!targets || !targets.length) {
      setActionMsg({ type: 'error', text: 'Select at least one participant.' });
      return;
    }
    setDeleteTargets(targets);
    setDeleteConfirmText('');
    setDeleteModalOpen(true);
  };

  const openDeleteModalFromBulk = () => {
    const targets = getTargets();
    openDeleteModal(targets);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
    setDeleteConfirmText('');
    setDeleteTargets([]);
  };

  const confirmHardDelete = async () => {
    if (deleteConfirmText.trim().toLowerCase() !== 'delete') return;
    if (!deleteTargets.length) return;
    setDeleting(true);
    try {
      const functions = getFunctions();
      const hardDelete = httpsCallable(functions, 'hardDeleteParticipants');
      const payload = {
        uids: deleteTargets.map(p => p.id),
        emails: deleteTargets.map(p => p.email),
        names: deleteTargets.map(p => p.name),
        companies: [...new Set(deleteTargets.map(p => p.company))],
      };
      const result = await hardDelete(payload);
      const data = result?.data || {};
      const deletedCount = data.deletedCount ?? deleteTargets.length;
      const failures = data.failures || [];

      setParticipants(prev => prev.filter(p => !deleteTargets.find(t => t.id === p.id)));
      setSelected({});
      setDeleteModalOpen(false);
      setDeleteConfirmText('');
      setDeleteTargets([]);
      setSelectedParticipant(null);

      if (failures.length) {
        setAdminMsg({
          type: 'error',
          text: `${deletedCount} participant${deletedCount !== 1 ? 's' : ''} permanently deleted. ${failures.length} failed: ${failures.map(f => f.email || f.uid).join(', ')}`,
        });
      } else {
        setAdminMsg({
          type: 'success',
          text: `${deletedCount} participant${deletedCount !== 1 ? 's' : ''} permanently deleted.`,
        });
      }
    } catch (err) {
      setAdminMsg({
        type: 'error',
        text: `Delete failed: ${err.message || 'Unknown error. Check that the Cloud Function is deployed and you are signed in as an authorized admin.'}`,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    import('xlsx').then(XLSX => {
      const rows = filtered.map(p => ({
        'Name': p.name, 'Email': p.email, 'Company': p.company,
        'Department': p.department, 'Job Title': p.role,
        'Pre Score': p.preScore || 'Not taken', 'Pre Level': p.preLevel,
        'Pre Frequency': p.preScores?.freq || '', 'Pre Confidence': p.preScores?.conf || '',
        'Pre Use Case Range': p.preScores?.range || '', 'Pre Advocacy': p.preScores?.adv || '',
        'Post Score': p.postScore || 'Not taken', 'Post Level': p.postLevel,
        'Post Frequency': p.postScores?.freq || '', 'Post Confidence': p.postScores?.conf || '',
        'Post Use Case Range': p.postScores?.range || '', 'Post Advocacy': p.postScores?.adv || '',
        'Score Change': (p.preScore && p.postScore) ? p.postScore - p.preScore : 'N/A',
        'Workbooks Completed': p.completedDays !== undefined ? p.completedDays : 'Loading',
        'Workbook Status': p.completedDays === undefined ? 'Loading' : (p.completedDays >= (p.assignedDays ?? 10) ? 'Complete' : (p.sectionsCompleted > 0 ? 'In progress' : 'Not started')),
        'Day Start': p.dayRangeStart || 'Default', 'Day End': p.dayRangeEnd || 'Default',
        'Video Access': p.cohortVideosEnabled ? 'Cohort On' : (p.videoAccess && p.videoAccess.length ? p.videoAccess.map(d => `D${d}`).join(', ') : 'None'),
        'Final Exam': p.examPassed ? 'Passed' : (p.examTaken ? 'In progress' : 'Not started'),
        'Exam Score': p.examScore ?? '',
        'Post Assessment': p.postAssessmentUnlocked ? 'Unlocked' : 'Locked',
        'Access': p.accessActive ? 'Active' : 'Ended',
        'Role': p.adminRole === 'companyadmin' ? 'Company Admin' : 'Participant',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Participants');
      XLSX.writeFile(wb, `TCF-AI-Readiness-${new Date().toISOString().slice(0, 10)}.xlsx`);
    });
  };

  const formatVideoAccess = (p) => {
    if (p.cohortVideosEnabled) return { label: 'Cohort On', color: '#1a8a4a' };
    const days = Array.isArray(p.videoAccess) ? [...p.videoAccess].sort((a, b) => a - b) : [];
    if (!days.length) return { label: 'None', color: '#3D3D3D' };
    let contiguous = true;
    for (let i = 1; i < days.length; i++) {
      if (days[i] !== days[i - 1] + 1) { contiguous = false; break; }
    }
    if (contiguous && days.length > 1) {
      return { label: `D${days[0]}–D${days[days.length - 1]}`, color: '#185fa5' };
    }
    return { label: days.map(d => `D${d}`).join(', '), color: '#185fa5' };
  };

  const sidebarItems = [
    { id: 'participants', label: 'All Participants' },
    { id: 'engagements', label: 'Client Engagements' },
    { id: 'invites', label: 'Invite Participants' },
    { id: 'emailtemplates', label: 'Email Wording' },
    { id: 'wins', label: 'Wins Wall' },
    { id: 'workbooks', label: 'Workbooks' },
    { id: 'usecasefinder', label: 'AI Use Case Finder' },
    { id: 'examresults', label: 'Final Exam Results' },
    { id: 'promptlibrary', label: 'Prompt Library' },
    { id: 'agentlibrary', label: 'Agent Library' },
    { id: 'references', label: 'References' },
    { id: 'managereferences', label: 'Manage References' },
    { id: 'resources', label: 'Resources Hub' },
    { id: 'manageresources', label: 'Manage Resources' },
    { id: 'managevideos', label: 'Manage Videos' },
    { id: 'team', label: 'TCF Team' },
  ];

  // PARTICIPANT DETAIL VIEW
  if (selectedParticipant) {
    const p = selectedParticipant;
    const wb = participantDetail?.workbook;
    const assessDetail = participantDetail?.assessment;
    const dayProgress = wb?.progress || {};
    const availableDays = Object.keys(dayProgress).map(k => parseInt(k.replace('day', ''))).filter(Boolean).sort((a, b) => a - b);

    // Support both 'sections' (Days 3-10 new format) and 'fields' (Day 1-2 old format)
    const dayEntry = dayProgress[`day${detailDay}`] || {};
    const dayData = dayEntry.sections || dayEntry.fields || {};

    const preS = p.preScores;
    const postS = p.postScores;
    const preOpenText = assessDetail?.pre?.openText;
    const postOpenText = assessDetail?.post?.openText;

    return (
      <div style={{ fontFamily: 'DM Sans, sans-serif', minHeight: '100vh', background: '#FAF8F5' }}>
        <nav style={styles.nav}>
          <div style={styles.navLogo}>The Corporate <span style={{ color: '#E87166' }}>Fixers</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={styles.navUser}>TCF Admin: <strong style={{ color: '#D4A847' }}>{user.email}</strong></div>
            <button style={styles.navLogout} onClick={() => auth.signOut()}>Log out</button>
          </div>
        </nav>
        <div style={styles.layout}>
          <aside style={styles.sidebar}>
            {sidebarItems.map(item => (
              <div key={item.id} style={{ ...styles.sidebarItem, ...(item.id === 'participants' ? styles.sidebarActive : {}) }}
                onClick={() => { if (item.id !== 'participants') { setSelectedParticipant(null); setAdminPage(item.id); } else { setSelectedParticipant(null); } }}>
                <div style={styles.dot}></div>{item.label}
              </div>
            ))}
          </aside>
          <main style={styles.main}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <button style={styles.backBtn} onClick={() => setSelectedParticipant(null)}>← Back to All Participants</button>
              <button
                style={{ ...styles.actionBtn, background: '#C0392B', fontWeight: '700' }}
                onClick={() => openDeleteModal([p])}
              >
                🗑 Delete Participant
              </button>
            </div>
            <div style={styles.heading}>{p.name}</div>
            <div style={styles.sub}>{p.department} · {p.role} · {p.email} · {p.company}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              {/* SCORES */}
              <div style={styles.card}>
                <div style={styles.cardTitle}>Assessment Scores</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ background: '#F2F0EC', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Pre Score</div>
                    <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: '900', color: '#1B2A4A' }}>{p.preScore || '—'}</div>
                    {p.preScore && <div style={{ fontSize: '11px', color: '#3D3D3D' }}>{p.preLevel}</div>}
                  </div>
                  <div style={{ background: '#1B2A4A', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#D4A847', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Post Score</div>
                    <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: '900', color: '#fff' }}>{p.postScore || '—'}</div>
                    {p.postScore && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>{p.postLevel}</div>}
                  </div>
                </div>
                {preS && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
                    {[['Freq', 'freq'], ['Conf', 'conf'], ['Range', 'range'], ['Adv', 'adv']].map(([label, key]) => (
                      <div key={key} style={{ textAlign: 'center', border: '1.5px solid #E8E6E2', padding: '8px' }}>
                        <div style={{ fontSize: '10px', color: '#1B2A4A', marginBottom: '2px' }}>{label}</div>
                        <div style={{ fontWeight: '600', color: '#1B2A4A', fontSize: '12px' }}>
                          {preS[key]}{postS ? `→${postS[key]}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(preOpenText || postOpenText) && (
                  <div style={{ marginTop: '16px', borderTop: '1px solid #E8E6E2', paddingTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '1px', textTransform: 'uppercase' }}>Open Text Responses</div>
                    {preOpenText && (
                      <div style={{ border: '1.5px solid #E8E6E2' }}>
                        <div style={{ background: '#F2F0EC', padding: '6px 12px', fontSize: '10px', fontWeight: '700', color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pre-Training</div>
                        <div style={{ padding: '10px 12px', fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{preOpenText}</div>
                      </div>
                    )}
                    {postOpenText && (
                      <div style={{ border: '1.5px solid #E8E6E2' }}>
                        <div style={{ background: '#1B2A4A', padding: '6px 12px', fontSize: '10px', fontWeight: '700', color: '#D4A847', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Post-Training</div>
                        <div style={{ padding: '10px 12px', fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{postOpenText}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ENGAGEMENT DEPTH */}
              <div style={styles.card}>
                <div style={styles.cardTitle}>Engagement Depth</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { label: 'Days completed', val: `${Object.values(dayProgress).filter(d => d.status === 'complete').length} / ${availableDays.length} started` },
                    { label: 'Sections with responses', val: Object.values(dayProgress).reduce((sum, d) => {
                        const data = d.sections || d.fields || {};
                        return sum + Object.keys(data).filter(k => typeof data[k] === 'string' && data[k]?.trim()).length;
                      }, 0) },
                    { label: 'Prompt Library entries', val: Object.values(dayProgress).reduce((sum, d) => { const s = d.sections || d.fields || {}; return sum + [1,2,3,4].filter(n => hasContent(s[`lib_${n}_prompt`])).length; }, 0) },
                  ].map(row => (
                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', border: '1.5px solid #E8E6E2' }}>
                      <div style={{ fontSize: '12px', color: '#1B2A4A' }}>{row.label}</div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#1B2A4A' }}>{row.val}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* WORKBOOK RESPONSES */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Workbook Responses</div>
              <div style={{ fontSize: '11px', color: '#3D3D3D', marginBottom: '16px' }}>Visible to TCF admin only. Company admins cannot see this content.</div>

              {availableDays.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#3D3D3D' }}>No workbook responses yet.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {availableDays.map(d => (
                      <button key={d}
                        style={{ ...styles.dayTab, ...(detailDay === d ? styles.dayTabActive : {}) }}
                        onClick={() => setDetailDay(d)}>
                        Day {d}
                      </button>
                    ))}
                  </div>
                  {loadingDetail ? (
                    <div style={{ fontSize: '13px', color: '#3D3D3D' }}>Loading...</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {Object.entries(dayData).filter(([, v]) => hasContent(v)).map(([key, val]) => (
                        <div key={key} style={{ border: '1.5px solid #E8E6E2' }}>
                          <div style={{ background: '#F2F0EC', padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {prettyKey(key)}
                          </div>
                          <div style={{ padding: '12px', fontSize: '13px', color: '#1B2A4A', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{renderAnswer(val)}</div>
                        </div>
                      ))}
                      {Object.keys(dayData).filter(k => hasContent(dayData[k])).length === 0 && (
                        <div style={{ fontSize: '13px', color: '#3D3D3D' }}>No responses for Day {detailDay} yet.</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
        <AdminFooter onPrivacy={() => { setSelectedParticipant(null); setAdminPage('privacy'); }} />
        {deleteModalOpen && (
          <DeleteConfirmModal
            targets={deleteTargets}
            confirmText={deleteConfirmText}
            onChangeText={setDeleteConfirmText}
            onCancel={closeDeleteModal}
            onConfirm={confirmHardDelete}
            deleting={deleting}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'DM Sans, sans-serif', minHeight: '100vh', background: '#FAF8F5' }}>
      <nav style={styles.nav}>
        <div style={styles.navLogo}>The Corporate <span style={{ color: '#E87166' }}>Fixers</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={styles.navUser}>TCF Admin: <strong style={{ color: '#D4A847' }}>{user.email}</strong></div>
          <button style={styles.navLogout} onClick={() => auth.signOut()}>Log out</button>
        </div>
      </nav>

      <div style={styles.layout}>
        <aside style={styles.sidebar}>
          {sidebarItems.map(item => (
            <div key={item.id}
              style={{ ...styles.sidebarItem, ...(adminPage === item.id ? styles.sidebarActive : {}) }}
              onClick={() => setAdminPage(item.id)}>
              <div style={styles.dot}></div>{item.label}
            </div>
          ))}
        </aside>

        <main style={styles.main}>
          {adminPage === 'engagements' && <Engagements />}
          {adminPage === 'invites' && <InviteParticipants user={user} mode="tcf" />}
          {adminPage === 'emailtemplates' && <EmailTemplates user={user} mode="tcf" />}
          {adminPage === 'wins' && <AdminWins />}
          {adminPage === 'workbooks' && <AdminWorkbookViewer user={user} />}
          {adminPage === 'usecasefinder' && <UseCaseFinder user={user} />}
          {adminPage === 'examresults' && <ExamResultsAdmin user={user} />}
          {adminPage === 'promptlibrary' && <PromptLibrary user={user} />}
          {adminPage === 'agentlibrary' && <AgentLibrary user={user} mode="admin" />}
          {adminPage === 'references' && <References />}
          {adminPage === 'managereferences' && <ManageReferences />}
          {adminPage === 'resources' && <ResourcesHub user={user} />}
          {adminPage === 'manageresources' && <ManageResourceTemplates user={user} />}
          {adminPage === 'managevideos' && <ManageVideos />}
          {adminPage === 'team' && <TCFTeam currentUser={user} />}
          {adminPage === 'privacy' && <PrivacyContent />}

          {adminPage === 'participants' && (
            <>
              <div style={styles.heading}>Admin Dashboard</div>
              <div style={styles.sub}>All participants across all active engagements. Click a name to view full detail.</div>

              {/* Floats so it is visible from anywhere in a long roster. */}
              {adminMsg && (
                <div
                  style={{
                    position: 'fixed', bottom: '24px', right: '24px', zIndex: 9500, maxWidth: '380px',
                    padding: '14px 18px', fontSize: '13px', lineHeight: 1.5,
                    boxShadow: '0 8px 28px rgba(27,42,74,0.28)',
                    background: adminMsg.type === 'error' ? '#fdecea' : '#eaf3de',
                    color: adminMsg.type === 'error' ? '#a32d2d' : '#3b6d11',
                    border: `1.5px solid ${adminMsg.type === 'error' ? '#e6c4c4' : '#c5ddaa'}`,
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                  onClick={() => setAdminMsg(null)}
                  title="Click to dismiss"
                >
                  {adminMsg.text}
                </div>
              )}

              <div style={styles.statsStrip}>
                {[
                  { label: 'Total Participants', val: companyScoped.length, sub: filters.company ? filters.company : `${companies.length} companies` },
                  { label: 'Invited vs Joined', val: `${joinedCount} / ${invitedCount}`, sub: invitedCount ? `${Math.round((joinedCount / invitedCount) * 100)}% of ${filters.company ? filters.company : 'all'} invites joined` : 'No invites yet' },
                  { label: 'Avg Pre Score', val: avgPre.toFixed(1), sub: 'Out of 20' },
                  { label: 'Avg Post Score', val: avgPost > 0 ? avgPost.toFixed(1) : '—', sub: avgPost > 0 ? `+${(avgPost - avgPre).toFixed(1)} avg gain` : 'Pending' },
                  { label: 'Completed Both', val: bothCompleted, sub: `${Math.round((bothCompleted / (companyScoped.length || 1)) * 100)}% completion` },
                ].map(s => (
                  <div key={s.label} style={styles.statCard}>
                    <div style={styles.statLabel}>{s.label}</div>
                    <div style={styles.statVal}>{s.val}</div>
                    <div style={styles.statSub}>{s.sub}</div>
                  </div>
                ))}
              </div>

              <div style={styles.journeyCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={styles.journeyTitle}>Where everyone is</div>
                  {invitedCount > 0 && (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[['invited', '% of invited'], ['registered', '% of registered']].map(([k, lbl]) => (
                        <button
                          key={k}
                          onClick={() => setJourneyBase(k)}
                          style={{
                            background: journeyBase === k ? '#1B2A4A' : '#F2F0EC',
                            color: journeyBase === k ? '#fff' : '#1B2A4A',
                            border: '1.5px solid #E8E6E2', padding: '5px 12px', fontSize: '11px',
                            fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                          }}
                        >{lbl}</button>
                      ))}
                    </div>
                  )}
                </div>
                {journey.map(stage => {
                  const pct = Math.round((stage.count / journeyTotal) * 100);
                  return (
                    <div key={stage.label} style={styles.journeyRow}>
                      <div style={styles.journeyLabel}>{stage.label}</div>
                      <div style={styles.journeyTrack}>
                        <div style={{ ...styles.journeyFill, width: `${Math.min(pct, 100)}%`, background: stage.gold ? '#D4A847' : '#1B2A4A' }} />
                      </div>
                      <div style={styles.journeyCount}>{stage.count}{stage.label !== 'Invited' && <span style={styles.journeyPct}> {pct}%</span>}</div>
                    </div>
                  );
                })}
              </div>

              <div style={styles.filterBar}>
                <select style={styles.select} value={filters.company} onChange={e => setFilters(f => ({ ...f, company: e.target.value }))}>
                  <option value="">All Companies</option>
                  {companies.map(c => <option key={c}>{c}</option>)}
                </select>
                <select style={styles.select} value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}>
                  <option value="">All Departments</option>
                  {departments.map(d => <option key={d}>{d}</option>)}
                </select>
                <select style={styles.select} value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}>
                  <option value="">All Job Titles</option>
                  {roles.map(r => <option key={r}>{r}</option>)}
                </select>
                <select style={styles.select} value={filters.level} onChange={e => setFilters(f => ({ ...f, level: e.target.value }))}>
                  <option value="">All Levels</option>
                  {LEVELS.map(l => <option key={l.label}>{l.label}</option>)}
                </select>
                <input style={styles.searchInput} placeholder="Search by name..." value={filters.name} onChange={e => setFilters(f => ({ ...f, name: e.target.value }))} />
                <button style={styles.exportBtn} onClick={handleExport}>⬇ Export to Excel</button>
                <button
                  style={styles.exportBtn}
                  onClick={() => { setDigestResult(null); setDigestOpen(true); }}
                >✉ Send exam pass emails</button>
              </div>

              {loading ? (
                <div style={{ color: '#3D3D3D', fontSize: '14px', padding: '20px' }}>Loading participants...</div>
              ) : (
                <>
                  <div style={styles.tableWrap}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}><input type="checkbox" checked={filtered.length > 0 && filtered.every(p => selected[p.id])} onChange={toggleSelectAll} /></th>
                          {[
                            ['Name', 'name'], ['Company', 'company'], ['Dept', 'department'], ['Job Title', 'role'],
                            ['Pre Score', 'preScore'], ['Post Score', 'postScore'], ['Change', 'change'], ['Level', 'level'],
                            ['Workbooks', 'workbooks'], ['Exam', 'exam'], ['Days', 'days'], ['Videos', 'videos'], ['Post Assess.', 'postAssess'],
                            ['Access', 'access'], ['Role', 'adminRole'], ['Actions', null],
                          ].map(([h, key]) => (
                            <th
                              key={h}
                              style={{ ...styles.th, ...(key ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } : {}) }}
                              onClick={key ? () => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 })) : undefined}
                              title={key ? 'Click to sort' : undefined}
                            >
                              {h}{key && sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 && (
                          <tr><td colSpan={17} style={{ padding: '20px', textAlign: 'center', color: '#3D3D3D', fontSize: '13px' }}>No participants match the current filters.</td></tr>
                        )}
                        {sorted.map(p => {
                          const delta = (p.preScore && p.postScore) ? p.postScore - p.preScore : null;
                          const currentLevel = p.postScore ? getLevel(p.postScore) : p.preScore ? getLevel(p.preScore) : null;
                          const isAdmin = p.adminRole === 'companyadmin';
                          return (
                            <tr key={p.id} style={{ ...styles.tr, ...(selected[p.id] ? styles.trSelected : {}), ...(isAdmin ? styles.trAdmin : {}) }}>
                              <td style={styles.td}><input type="checkbox" checked={!!selected[p.id]} onChange={() => toggleSelect(p.id)} /></td>
                              <td style={styles.td}>
                                <div style={{ fontWeight: '600', color: '#C0392B', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => loadParticipantDetail(p)}>
                                  {p.name}
                                </div>
                                <div style={styles.emailCell}>{p.email}</div>
                                <div style={styles.editLinkRow}>
                                  <button style={styles.emailEditLink} onClick={() => openEmailEdit(p)}>Edit email</button>
                                <button style={styles.emailEditLink} onClick={() => handleSendReset(p)} disabled={!!resetBusy[p.id]}>
                                  {resetBusy[p.id] ? 'Working…' : 'Reset password'}
                                </button>
                                <button style={styles.emailEditLink} onClick={() => handleTempPassword(p)} disabled={!!resetBusy[p.id]}>
                                  Set temp password
                                </button>
                                  <button style={styles.emailEditLink} onClick={() => openDetailsEdit(p)}>Edit dept / title</button>
                                </div>
                              </td>
                              <td style={styles.td}>{p.company}</td>
                              <td style={styles.td}>{p.department}</td>
                              <td style={styles.td}>{p.role}</td>
                              <td style={{ ...styles.td, textAlign: 'center' }}>{p.preScore || '—'}</td>
                              <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600' }}>{p.postScore || '—'}</td>
                              <td style={{ ...styles.td, textAlign: 'center', color: delta > 0 ? '#1a8a4a' : '#3D3D3D', fontWeight: '600' }}>
                                {delta !== null ? (delta > 0 ? `+${delta}` : delta) : 'Pending'}
                              </td>
                              <td style={styles.td}>
                                {currentLevel && <span style={{ ...styles.badge, ...levelColors[currentLevel.cls] }}>{currentLevel.label}</span>}
                              </td>
                              <td style={{ ...styles.td, textAlign: 'center' }}>
                                {p.completedDays === undefined
                                  ? <span style={{ fontSize: '11px', color: '#8A8A8A' }}>...</span>
                                  : (
                                    <>
                                      <span style={{ fontSize: '11px', fontWeight: '600', background: '#F2F0EC', color: '#1B2A4A', padding: '2px 8px', display: 'inline-block' }}>
                                        {p.completedDays} / {p.assignedDays ?? 10}
                                      </span>
                                      {p.completedDays < (p.assignedDays ?? 10) && p.sectionsCompleted > 0 && (
                                        <div style={{ fontSize: '10px', color: '#854f0b', marginTop: '2px', fontWeight: '600' }}>In progress</div>
                                      )}
                                    </>
                                  )}
                              </td>
                              <td style={{ ...styles.td, textAlign: 'center' }}>
                                {p.examPassed
                                  ? (
                                    <>
                                      <span style={{ fontSize: '11px', fontWeight: '600', background: '#eaf3de', color: '#3b6d11', padding: '2px 8px', whiteSpace: 'nowrap' }}>✓ Passed{p.examScore ? ` ${p.examScore}` : ''}</span>
                                      <div>
                                        <button
                                          style={styles.emailEditLink}
                                          disabled={resendingUid === p.id}
                                          onClick={() => handleResendPassEmail(p)}
                                        >{resendingUid === p.id ? 'Sending...' : 'Resend email'}</button>
                                      </div>
                                    </>
                                  )
                                  : p.examTaken
                                    ? <span style={{ fontSize: '11px', fontWeight: '600', background: '#fdf3e2', color: '#854f0b', padding: '2px 8px', whiteSpace: 'nowrap' }}>In progress{p.examScore ? ` ${p.examScore}` : ''}</span>
                                    : <span style={{ fontSize: '11px', color: '#8A8A8A' }}>Not started</span>}
                              </td>
                              <td style={styles.td}>
                                {p.dayRangeStart && p.dayRangeEnd
                                  ? <span style={styles.daysBadge}>D{p.dayRangeStart}–D{p.dayRangeEnd}</span>
                                  : <span style={{ fontSize: '11px', color: '#3D3D3D' }}>Default</span>}
                              </td>
                              <td style={styles.td}>
                                {(() => {
                                  const v = formatVideoAccess(p);
                                  return <span style={{ fontSize: '11px', fontWeight: '600', color: v.color }}>{v.label}</span>;
                                })()}
                              </td>
                              <td style={styles.td}>
                                <span style={{ fontSize: '11px', fontWeight: '600', color: p.postAssessmentUnlocked ? '#1a8a4a' : '#3D3D3D' }}>
                                  {p.postAssessmentUnlocked ? 'Unlocked' : 'Locked'}
                                </span>
                              </td>
                              <td style={styles.td}>
                                <span style={{ fontSize: '11px', fontWeight: '600', color: p.accessActive ? '#1a8a4a' : '#C0392B' }}>
                                  {p.accessActive ? 'Active' : 'Ended'}
                                </span>
                              </td>
                              <td style={styles.td}>
                                {isAdmin
                                  ? <span style={styles.adminBadge}>Co. Admin</span>
                                  : <span style={{ fontSize: '11px', color: '#3D3D3D' }}>Participant</span>}
                              </td>
                              <td style={styles.td}>
                                {isAdmin
                                  ? <button style={styles.removeAdminBtn} onClick={() => removeAdmin(p)}>Remove Admin</button>
                                  : <button style={styles.makeAdminBtn} onClick={() => makeAdmin(p)}>Make Admin</button>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {digestOpen && (
                    <div style={styles.emailModalOverlay} onClick={() => !digestBusy && setDigestOpen(false)}>
                      <div style={styles.emailModalCard} onClick={e => e.stopPropagation()}>
                        <div style={styles.emailModalTitle}>Send exam pass emails</div>
                        <div style={styles.emailModalSub}>
                          These go out on their own every day at 4pm Arizona time. This sends them now.
                        </div>
                        <div style={styles.emailModalNote}>
                          Everyone who passed gets a congratulations email with a link to their certificate.
                          You get a summary, and each company&rsquo;s leads get a summary of their own people.
                          Nobody is emailed twice, so it is safe to press this more than once.
                        </div>

                        {digestResult && !digestResult.error && (
                          <div style={{ background: '#EAF6EE', color: '#1a6b3a', padding: '12px 14px', fontSize: '13px', margin: '14px 0', lineHeight: 1.6 }}>
                            {digestResult.passes === 0
                              ? 'Nobody new to email. Everyone who passed has already been told.'
                              : `Sent. ${digestResult.passes} ${digestResult.passes === 1 ? 'person' : 'people'}, ${digestResult.emailsSent} emails.`}
                            {digestResult.failures && digestResult.failures.length > 0 && (
                              <div style={{ color: '#a32d2d', marginTop: '6px' }}>
                                {digestResult.failures.length} didn&rsquo;t send. Check SendGrid.
                              </div>
                            )}
                          </div>
                        )}
                        {digestResult && digestResult.error && (
                          <div style={{ background: '#fdecea', color: '#a32d2d', padding: '12px 14px', fontSize: '13px', margin: '14px 0' }}>
                            {digestResult.error}
                          </div>
                        )}

                        <div style={styles.emailModalActions}>
                          <button
                            style={styles.actionBtn}
                            disabled={digestBusy}
                            onClick={() => handleRunDigest(false)}
                          >{digestBusy ? 'Sending...' : 'Just the last day'}</button>
                          <button
                            style={styles.actionBtn}
                            disabled={digestBusy}
                            onClick={() => {
                              if (window.confirm('This emails everyone who has ever passed and hasn’t been emailed yet, including people who passed weeks ago. Send them?')) {
                                handleRunDigest(true);
                              }
                            }}
                          >{digestBusy ? 'Sending...' : 'Catch up on everyone'}</button>
                          <button style={styles.emailModalSave} onClick={() => setDigestOpen(false)} disabled={digestBusy}>Close</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {tempPwResult && (
                    <div style={styles.emailModalOverlay} onClick={() => setTempPwResult(null)}>
                      <div style={styles.emailModalCard} onClick={e => e.stopPropagation()}>
                        <div style={styles.emailModalTitle}>Temporary password set</div>
                        <div style={styles.emailModalSub}>{tempPwResult.name || tempPwResult.email}</div>
                        <div style={{ background: '#F2F0EC', border: '1.5px dashed #D4A847', padding: '16px', textAlign: 'center', margin: '14px 0' }}>
                          <div style={{ fontFamily: 'Consolas, Menlo, monospace', fontSize: '22px', fontWeight: '700', letterSpacing: '2px', color: '#1B2A4A' }}>
                            {tempPwResult.tempPassword}
                          </div>
                        </div>
                        <div style={styles.emailModalNote}>
                          Read this to them over the phone or a chat message. Do not email it. This is the only
                          time it appears, so copy it before you close this box. They sign in with their usual
                          email address, and the portal will ask them to choose their own password right after.
                        </div>
                        <div style={styles.emailModalActions}>
                          <button
                            style={styles.actionBtn}
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(tempPwResult.tempPassword);
                                setTempPwCopied(true);
                                setTimeout(() => setTempPwCopied(false), 2500);
                              } catch { /* clipboard unavailable */ }
                            }}
                          >{tempPwCopied ? '✓ Copied' : 'Copy password'}</button>
                          <button style={styles.emailModalSave} onClick={() => setTempPwResult(null)}>Done</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {emailEdit && (
                    <div style={styles.emailModalOverlay} onClick={() => !emailEditSaving && setEmailEdit(null)}>
                      <div style={styles.emailModalCard} onClick={e => e.stopPropagation()}>
                        <div style={styles.emailModalTitle}>Edit email</div>
                        <div style={styles.emailModalSub}>{emailEdit.name || emailEdit.email}</div>
                        <input
                          style={styles.emailModalInput}
                          type="email"
                          value={emailEditValue}
                          onChange={e => setEmailEditValue(e.target.value)}
                          placeholder="new.email@company.com"
                        />
                        <div style={styles.emailModalNote}>
                          This changes the participant's login email. They will sign in with the new address from now on.
                        </div>
                        <div style={styles.emailModalActions}>
                          <button style={styles.actionBtn} onClick={() => setEmailEdit(null)} disabled={emailEditSaving}>Cancel</button>
                          <button style={styles.emailModalSave} onClick={handleSaveEmail} disabled={emailEditSaving}>
                            {emailEditSaving ? 'Saving…' : 'Save email'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {detailsEdit && (
                    <div style={styles.emailModalOverlay} onClick={() => !detailsEditSaving && setDetailsEdit(null)}>
                      <div style={styles.emailModalCard} onClick={e => e.stopPropagation()}>
                        <div style={styles.emailModalTitle}>Edit department and job title</div>
                        <div style={styles.emailModalSub}>{detailsEdit.name || detailsEdit.email}</div>
                        <div style={styles.editModalField}>
                          <label style={styles.editModalLabel}>Department</label>
                          <input
                            style={styles.emailModalInput}
                            type="text"
                            value={detailsEditValue.department}
                            onChange={e => setDetailsEditValue(v => ({ ...v, department: e.target.value }))}
                            placeholder="e.g. HR, Marketing, Finance"
                          />
                        </div>
                        <div style={styles.editModalField}>
                          <label style={styles.editModalLabel}>Job Title</label>
                          <input
                            style={styles.emailModalInput}
                            type="text"
                            value={detailsEditValue.jobTitle}
                            onChange={e => setDetailsEditValue(v => ({ ...v, jobTitle: e.target.value }))}
                            placeholder="e.g. HR Manager"
                          />
                        </div>
                        <div style={styles.emailModalNote}>
                          The participant can also change these on their own Account Settings page. Past exam records keep the title they had at the time.
                        </div>
                        <div style={styles.emailModalActions}>
                          <button style={styles.actionBtn} onClick={() => setDetailsEdit(null)} disabled={detailsEditSaving}>Cancel</button>
                          <button style={styles.emailModalSave} onClick={handleSaveDetails} disabled={detailsEditSaving}>
                            {detailsEditSaving ? 'Saving…' : 'Save changes'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={styles.bulkPanel}>
                    <div style={styles.bulkLabel}>
                      {selectedCount > 0 ? `${selectedCount} selected — take action` : 'Select participants above to take action'}
                    </div>
                    {selectedCount > 0 && (
                      <div style={styles.bulkActions}>
                        <div style={styles.bulkGroup}>
                          <div style={styles.bulkGroupLabel}>Change Day Range</div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <select style={styles.smallSelect} value={bulkAction.dayRangeStart} onChange={e => setBulkAction(b => ({ ...b, dayRangeStart: e.target.value }))}>
                              <option value="">Start</option>
                              {ALL_DAYS.map(d => <option key={d} value={d}>Day {d}</option>)}
                            </select>
                            <span style={{ fontSize: '12px', color: '#3D3D3D' }}>to</span>
                            <select style={styles.smallSelect} value={bulkAction.dayRangeEnd} onChange={e => setBulkAction(b => ({ ...b, dayRangeEnd: e.target.value }))}>
                              <option value="">End</option>
                              {ALL_DAYS.map(d => <option key={d} value={d}>Day {d}</option>)}
                            </select>
                            <button style={styles.actionBtn} onClick={applyDayRange}>Apply</button>
                          </div>
                        </div>
                        <div style={styles.bulkGroup}>
                          <div style={styles.bulkGroupLabel}>Post Assessment</div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button style={styles.actionBtn} onClick={() => applyPostUnlock(true)}>Unlock</button>
                            <button style={{ ...styles.actionBtn, background: '#fff', color: '#1B2A4A', border: '1.5px solid #E8E6E2' }} onClick={() => applyPostUnlock(false)}>Lock</button>
                          </div>
                        </div>
                        <div style={styles.bulkGroup}>
                          <div style={styles.bulkGroupLabel}>Access</div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button style={styles.actionBtn} onClick={() => applyAccess(true)}>Restore</button>
                            <button style={{ ...styles.actionBtn, background: '#C0392B' }} onClick={() => applyAccess(false)}>End Access</button>
                          </div>
                        </div>
                        <div style={styles.bulkGroup}>
                          <div style={styles.bulkGroupLabel}>Reminders</div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button style={styles.actionBtn} onClick={() => handleSendReminders(filtered.filter(p => selected[p.id]))}>Send reminder</button>
                          </div>
                        </div>
                        <div style={styles.bulkGroup}>
                          <div style={styles.bulkGroupLabel}>Video Access</div>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '320px' }}>
                            {ALL_DAYS.map(d => (
                              <button
                                key={d}
                                onClick={() => toggleVideoDayChip(d)}
                                style={{
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  padding: '5px 10px',
                                  cursor: 'pointer',
                                  fontFamily: 'DM Sans, sans-serif',
                                  border: '1.5px solid',
                                  borderColor: bulkVideoDays.includes(d) ? '#1B2A4A' : '#E8E6E2',
                                  background: bulkVideoDays.includes(d) ? '#1B2A4A' : '#fff',
                                  color: bulkVideoDays.includes(d) ? '#D4A847' : '#3D3D3D',
                                }}
                              >
                                D{d}
                              </button>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                            <button style={styles.actionBtn} onClick={applyVideoAccess}>Grant</button>
                            <button style={{ ...styles.actionBtn, background: '#fff', color: '#1B2A4A', border: '1.5px solid #E8E6E2' }} onClick={clearVideoAccess}>Clear All</button>
                          </div>
                        </div>
                        <div style={{ ...styles.bulkGroup, borderLeft: '3px solid #C0392B', paddingLeft: '14px' }}>
                          <div style={{ ...styles.bulkGroupLabel, color: '#C0392B' }}>Danger Zone</div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              style={{ ...styles.actionBtn, background: '#C0392B', fontWeight: '700' }}
                              onClick={openDeleteModalFromBulk}
                            >
                              🗑 Delete Selected
                            </button>
                          </div>
                          <div style={{ fontSize: '10px', color: '#8A8A8A', marginTop: '6px', maxWidth: '220px', lineHeight: '1.4' }}>
                            Permanently erases all data. Cannot be undone.
                          </div>
                        </div>
                      </div>
                    )}
                    {actionMsg && (
                      <div style={{ ...styles.msgBox, ...(actionMsg.type === 'error' ? styles.msgError : styles.msgSuccess), marginTop: '10px' }}>
                        {actionMsg.text}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </div>
      <AdminFooter onPrivacy={() => setAdminPage('privacy')} />
      {deleteModalOpen && (
        <DeleteConfirmModal
          targets={deleteTargets}
          confirmText={deleteConfirmText}
          onChangeText={setDeleteConfirmText}
          onCancel={closeDeleteModal}
          onConfirm={confirmHardDelete}
          deleting={deleting}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({ targets, confirmText, onChangeText, onCancel, onConfirm, deleting }) {
  const count = targets.length;
  const companies = [...new Set(targets.map(t => t.company).filter(Boolean))];
  const canConfirm = confirmText.trim().toLowerCase() === 'delete' && !deleting;

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.box}>
        <div style={modalStyles.header}>
          <div style={modalStyles.icon}>⚠</div>
          <div style={modalStyles.title}>Permanently Delete {count === 1 ? 'Participant' : 'Participants'}</div>
        </div>

        <div style={modalStyles.body}>
          <p style={modalStyles.lead}>
            You are about to permanently delete <strong>{count} participant{count !== 1 ? 's' : ''}</strong>
            {companies.length > 0 && <> from <strong>{companies.length} {companies.length === 1 ? 'company' : 'companies'}</strong> ({companies.join(', ')})</>}.
          </p>
          <p style={modalStyles.body2}>
            This wipes every record of them from the portal. Their logins, profiles, workbook responses, assessment scores, wins posts, certificates, prompt library, and any other data will be erased from both Firestore and Firebase Authentication. This cannot be undone.
          </p>

          {count <= 10 && (
            <div style={modalStyles.nameList}>
              {targets.map(t => (
                <div key={t.id} style={modalStyles.nameRow}>
                  <span style={{ fontWeight: '600', color: '#1B2A4A' }}>{t.name}</span>
                  <span style={{ color: '#8A8A8A', fontSize: '12px', marginLeft: '8px' }}>{t.email}</span>
                </div>
              ))}
            </div>
          )}
          {count > 10 && (
            <div style={{ ...modalStyles.nameList, textAlign: 'center', color: '#3D3D3D', fontSize: '13px' }}>
              {count} participants selected. Review your selection before confirming.
            </div>
          )}

          <div style={modalStyles.confirmLabel}>
            Type <strong>delete</strong> to confirm:
          </div>
          <input
            type="text"
            value={confirmText}
            onChange={e => onChangeText(e.target.value)}
            disabled={deleting}
            placeholder="delete"
            style={modalStyles.input}
            autoFocus
          />
        </div>

        <div style={modalStyles.footer}>
          <button
            style={{ ...modalStyles.btn, background: '#fff', color: '#1B2A4A', border: '1.5px solid #E8E6E2' }}
            onClick={onCancel}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            style={{
              ...modalStyles.btn,
              background: canConfirm ? '#C0392B' : '#E8E6E2',
              color: canConfirm ? '#fff' : '#8A8A8A',
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {deleting ? 'Deleting...' : 'Delete Forever'}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(27, 42, 74, 0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  box: {
    background: '#fff',
    maxWidth: '560px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    border: '2px solid #C0392B',
    fontFamily: 'DM Sans, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px 24px',
    borderBottom: '1.5px solid #E8E6E2',
    background: '#FDF2F0',
  },
  icon: {
    fontSize: '28px',
    color: '#C0392B',
  },
  title: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '22px',
    fontWeight: '900',
    color: '#1B2A4A',
  },
  body: {
    padding: '20px 24px',
    overflowY: 'auto',
  },
  lead: {
    fontSize: '14px',
    color: '#1B2A4A',
    lineHeight: '1.6',
    margin: '0 0 12px 0',
  },
  body2: {
    fontSize: '13px',
    color: '#3D3D3D',
    lineHeight: '1.7',
    margin: '0 0 16px 0',
  },
  nameList: {
    background: '#FAF8F5',
    border: '1.5px solid #E8E6E2',
    padding: '10px 14px',
    maxHeight: '160px',
    overflowY: 'auto',
    marginBottom: '18px',
  },
  nameRow: {
    fontSize: '13px',
    padding: '4px 0',
    borderBottom: '1px solid #E8E6E2',
  },
  confirmLabel: {
    fontSize: '13px',
    color: '#1B2A4A',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1.5px solid #C0392B',
    fontFamily: 'DM Sans, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '16px 24px',
    borderTop: '1.5px solid #E8E6E2',
    background: '#FAF8F5',
  },
  btn: {
    padding: '10px 20px',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: 'DM Sans, sans-serif',
    border: 'none',
    cursor: 'pointer',
  },
};

function AdminFooter({ onPrivacy }) {
  return (
    <footer style={{ width: '100%', padding: '16px 32px', borderTop: '1px solid #E8E6E2', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'DM Sans, sans-serif', boxSizing: 'border-box' }}>
      <span style={{ fontSize: '12px', color: '#8A8A8A' }}>© {new Date().getFullYear()} The Corporate Fixers. All rights reserved.</span>
      <button onClick={onPrivacy} style={{ fontSize: '12px', color: '#1B2A4A', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' }}>
        Privacy Statement
      </button>
    </footer>
  );
}

function PrivacyContent() {
  const EFFECTIVE_DATE = 'March 30, 2026';
  const ps = {
    page: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '48px 56px' },
    eyebrow: { fontSize: '11px', fontFamily: 'DM Sans, sans-serif', letterSpacing: '2px', color: '#8A8A8A', marginBottom: '8px', fontWeight: '600' },
    title: { fontFamily: 'Playfair Display, serif', fontSize: '32px', fontWeight: '900', color: '#1B2A4A', marginBottom: '4px' },
    subtitle: { fontFamily: 'DM Sans, sans-serif', fontSize: '18px', color: '#555555', fontWeight: '300', marginBottom: '8px' },
    date: { fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#8A8A8A', fontStyle: 'italic', marginBottom: '32px' },
    divider: { borderTop: '1.5px solid #E8E6E2', margin: '32px 0' },
    heading: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: '#1B2A4A', marginTop: '32px', marginBottom: '10px' },
    body: { fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#3D3D3D', lineHeight: '1.8', fontWeight: '300', marginBottom: '12px' },
    list: { paddingLeft: '24px', marginBottom: '16px' },
    listItem: { fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#3D3D3D', lineHeight: '1.8', fontWeight: '300', marginBottom: '6px' },
    link: { color: '#1B2A4A', fontWeight: '600' },
    contact: { background: '#FAF8F5', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginTop: '16px', marginBottom: '8px' },
    contactName: { fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
    contactLine: { fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#3D3D3D', fontWeight: '300', lineHeight: '1.8' },
    footer: { fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#8A8A8A', fontStyle: 'italic', lineHeight: '1.6' },
  };
  const H = ({ children }) => <div style={ps.heading}>{children}</div>;
  const P = ({ children }) => <p style={ps.body}>{children}</p>;
  const B = ({ items }) => <ul style={ps.list}>{items.map((t, i) => <li key={i} style={ps.listItem}>{t}</li>)}</ul>;
  return (
    <div style={ps.page}>
      <div style={ps.eyebrow}>THE CORPORATE FIXERS</div>
      <div style={ps.title}>TCF AI Portal</div>
      <div style={ps.subtitle}>Data Privacy Statement</div>
      <div style={ps.date}>Effective Date: {EFFECTIVE_DATE}</div>
      <div style={ps.divider} />
      <P>The Corporate Fixers (TCF) is committed to handling participant data responsibly, transparently, and with respect for the organizations and individuals we serve. This statement explains what data we collect through the TCF AI Portal, how it is used, who can access it, and how it is protected.</P>
      <H>Who This Applies To</H>
      <P>This statement applies to all individuals who access the TCF AI Portal as part of a corporate training engagement facilitated by The Corporate Fixers. This includes program participants, company administrators, and TCF staff.</P>
      <H>What Data We Collect</H>
      <P>When a participant registers and engages with the TCF AI Portal, the following data is collected:</P>
      <B items={['Name and email address used to create the account','Job title and department (as entered during registration)','Company name and assigned engagement code','Workbook responses submitted during training exercises','Pre- and post-assessment scores','Program completion status and certificate records','Wins Wall posts, if the participant chooses to share them']} />
      <P>We do not collect payment information, government-issued identification, health information, or any data unrelated to program participation.</P>
      <H>How Data Is Used</H>
      <P>Participant data is used exclusively for the following purposes:</P>
      <B items={['Delivering and tracking progress through the training program','Generating completion certificates upon program requirements being met','Enabling company administrators to monitor engagement-level participation and completion','Providing TCF facilitators with the context needed to support participants effectively']} />
      <P>Participant data is never used to train AI models, sold to third parties, shared with advertisers, or used for any purpose outside the scope of the training engagement.</P>
      <H>Who Can Access Your Data</H>
      <P>Access to participant data is enforced at the database level through a four-tier permission structure:</P>
      <B items={['Participants can only access their own records','Company administrators can view completion data and assessment scores for participants within their assigned engagement only','TCF administrators can access data only for companies explicitly assigned to them by TCF leadership','Tamica Sears, as the primary administrator of The Corporate Fixers, has full access to all engagement data for program management purposes']} />
      <P>No participant can access another participant's data. No company administrator can access data outside their assigned engagement.</P>
      <H>How Data Is Protected</H>
      <P>The TCF AI Portal is built on Google Firebase, a cloud infrastructure platform used by enterprise organizations worldwide. Data protections include:</P>
      <B items={["All data is encrypted in transit using HTTPS and encrypted at rest within Google's infrastructure","Authentication is handled by Firebase Authentication. Passwords are never stored in plain text","Access control rules are enforced at the database level, not solely within the application interface","Each client engagement is isolated by a unique company code. Cross-company data access is not permitted by the system architecture"]} />
      <H>Data Retention and Deletion</H>
      <P>The Corporate Fixers retains participant data for the duration of the engagement and for a reasonable period thereafter to support certificate verification and program records.</P>
      <P>If your organization requires that participant data be deleted, submit a written deletion request to <a href="mailto:tamica@thecorporatefixers.com" style={ps.link}>tamica@thecorporatefixers.com</a>. All requested data will be permanently removed from the TCF AI Portal within 30 days of the confirmed request.</P>
      <H>Third-Party Services</H>
      <P>The TCF AI Portal operates on the following third-party infrastructure:</P>
      <B items={['Google Firebase: Authentication and database services','Google Firestore: Encrypted cloud data storage','Netlify: Application hosting and deployment']} />
      <P>These platforms are governed by their own privacy and security policies. TCF does not share participant data with these providers beyond what is technically necessary to operate the portal.</P>
      <H>Your Rights</H>
      <P>Participants and client organizations have the right to:</P>
      <B items={['Request a summary of data stored for their account or engagement','Request correction of inaccurate information','Request deletion of all data associated with their engagement']} />
      <P>To exercise any of these rights, contact The Corporate Fixers directly at <a href="mailto:tamica@thecorporatefixers.com" style={ps.link}>tamica@thecorporatefixers.com</a>.</P>
      <H>Questions and Contact</H>
      <P>If you have questions about this statement or how your organization's data is handled, reach out directly.</P>
      <div style={ps.contact}>
        <div style={ps.contactName}>Tamica Sears</div>
        <div style={ps.contactLine}>The Corporate Fixers</div>
        <div style={ps.contactLine}><a href="mailto:tamica@thecorporatefixers.com" style={ps.link}>tamica@thecorporatefixers.com</a></div>
      </div>
      <div style={ps.divider} />
      <div style={ps.footer}>This statement is reviewed and updated as needed. The effective date above reflects the most recent revision.</div>
    </div>
  );
}

function TCFTeam({ currentUser }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newMemberCompanies, setNewMemberCompanies] = useState([]);
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editCompanies, setEditCompanies] = useState([]);

  useEffect(() => { loadTeam(); loadEngagements(); }, []);

  const loadEngagements = async () => {
    const snap = await getDocs(collection(db, 'engagements'));
    setEngagements(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const loadTeam = async () => {
    const snap = await getDocs(collection(db, 'tcfAdmins'));
    setTeamMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  const toggleCompany = (code, current, setter) => {
    setter(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const addTeamMember = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true);
    setMsg(null);
    try {
      // Write to tcfAdmins collection (used by security rules)
      await setDoc(doc(db, 'tcfAdmins', email), {
        email,
        companyCodes: newMemberCompanies,
        addedAt: new Date().toISOString(),
        addedBy: currentUser.email,
      });
      // Also update their user document if they have one registered
      const usersSnap = await getDocs(query(collection(db, 'users'), where('email', '==', email)));
      if (!usersSnap.empty) {
        await updateDoc(doc(db, 'users', usersSnap.docs[0].id), { adminRole: 'tcfadmin' });
      }
      setNewEmail('');
      setNewMemberCompanies([]);
      setMsg({ type: 'success', text: `${email} added as TCF admin${newMemberCompanies.length ? ` for ${newMemberCompanies.length} company(s)` : ' with access to all companies'}.` });
      await loadTeam();
    } catch (err) {
      setMsg({ type: 'error', text: 'Something went wrong. Please try again.' });
    }
    setAdding(false);
  };

  const saveCompanyAssignments = async (memberEmail) => {
    try {
      await updateDoc(doc(db, 'tcfAdmins', memberEmail), { companyCodes: editCompanies });
      setTeamMembers(prev => prev.map(m => m.id === memberEmail ? { ...m, companyCodes: editCompanies } : m));
      setEditingMember(null);
      setMsg({ type: 'success', text: 'Company assignments updated.' });
      setTimeout(() => setMsg(null), 3000);
    } catch {
      setMsg({ type: 'error', text: 'Could not save assignments. Please try again.' });
    }
  };

  const removeTeamMember = async (memberEmail) => {
    if (memberEmail === currentUser.email) { setMsg({ type: 'error', text: 'You cannot remove yourself.' }); return; }
    try {
      // Remove from tcfAdmins collection
      await deleteDoc(doc(db, 'tcfAdmins', memberEmail));
      // Remove adminRole from user document if they have one
      const usersSnap = await getDocs(query(collection(db, 'users'), where('email', '==', memberEmail)));
      if (!usersSnap.empty) {
        await updateDoc(doc(db, 'users', usersSnap.docs[0].id), { adminRole: 'participant' });
      }
      setTeamMembers(prev => prev.filter(m => m.id !== memberEmail));
      setMsg({ type: 'success', text: `${memberEmail} removed from TCF team.` });
      setTimeout(() => setMsg(null), 3000);
    } catch {
      setMsg({ type: 'error', text: 'Could not remove member. Please try again.' });
    }
  };

  const companyLabel = (code) => {
    const eng = engagements.find(e => e.id === code);
    return eng ? `${eng.company} (${code})` : code;
  };

  return (
    <div style={{ maxWidth: '700px' }}>
      <div style={styles.heading}>TCF Team</div>
      <div style={styles.sub}>TCF admins have full access to the companies you assign them to. Leave company assignments blank to give access to all companies.</div>

      {msg && (
        <div style={{ ...styles.msgBox, ...(msg.type === 'error' ? styles.msgError : styles.msgSuccess), marginBottom: '16px' }}>
          {msg.text}
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.cardTitle}>Add a TCF Admin</div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '6px' }}>Email Address</label>
          <input
            style={{ ...styles.inputField, width: '100%', boxSizing: 'border-box' }}
            type="email"
            placeholder="colleague@thecorporatefixers.com"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
          />
        </div>
        {engagements.length > 0 && (
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '8px' }}>
              Assign to Companies <span style={{ fontWeight: 300, textTransform: 'none', letterSpacing: 0 }}>(leave blank for all companies)</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {engagements.map(eng => (
                <label key={eng.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#3D3D3D', cursor: 'pointer', background: newMemberCompanies.includes(eng.id) ? '#1B2A4A' : '#F2F0EC', color: newMemberCompanies.includes(eng.id) ? '#D4A847' : '#3D3D3D', padding: '5px 10px', fontWeight: newMemberCompanies.includes(eng.id) ? '600' : '400' }}>
                  <input type="checkbox" checked={newMemberCompanies.includes(eng.id)} onChange={() => toggleCompany(eng.id, newMemberCompanies, setNewMemberCompanies)} style={{ display: 'none' }} />
                  {eng.company || eng.id}
                </label>
              ))}
            </div>
          </div>
        )}
        <button style={styles.addBtn} onClick={addTeamMember} disabled={adding || !newEmail.trim()}>
          {adding ? 'Adding...' : 'Add TCF Admin →'}
        </button>
      </div>

      <div style={{ marginTop: '24px' }}>
        <div style={styles.cardTitle}>Current TCF Admins</div>
        {loading ? (
          <div style={{ color: '#3D3D3D', fontSize: '13px', marginTop: '12px' }}>Loading...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
            {teamMembers.length === 0 && (
              <div style={{ fontSize: '13px', color: '#3D3D3D' }}>No TCF admins added yet.</div>
            )}
            {teamMembers.map(m => (
              <div key={m.id} style={styles.memberRow}>
                <div style={{ flex: 1 }}>
                  <div style={styles.memberName}>{m.email}</div>
                  {editingMember === m.id ? (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#8A8A8A', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Company Assignments</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                        {engagements.map(eng => (
                          <label key={eng.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', background: editCompanies.includes(eng.id) ? '#1B2A4A' : '#F2F0EC', color: editCompanies.includes(eng.id) ? '#D4A847' : '#3D3D3D', padding: '5px 10px', fontWeight: editCompanies.includes(eng.id) ? '600' : '400' }}>
                            <input type="checkbox" checked={editCompanies.includes(eng.id)} onChange={() => toggleCompany(eng.id, editCompanies, setEditCompanies)} style={{ display: 'none' }} />
                            {eng.company || eng.id}
                          </label>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={styles.actionBtn} onClick={() => saveCompanyAssignments(m.id)}>Save</button>
                        <button style={{ ...styles.actionBtn, background: '#fff', color: '#3D3D3D', border: '1.5px solid #E8E6E2' }} onClick={() => setEditingMember(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: '#8A8A8A', marginTop: '3px' }}>
                      {m.companyCodes && m.companyCodes.length > 0
                        ? `Assigned to: ${m.companyCodes.map(c => engagements.find(e => e.id === c)?.company || c).join(', ')}`
                        : 'All companies'}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {editingMember !== m.id && (
                    <button style={{ ...styles.removeBtn, color: '#1B2A4A', borderColor: '#E8E6E2' }} onClick={() => { setEditingMember(m.id); setEditCompanies(m.companyCodes || []); }}>
                      Edit
                    </button>
                  )}
                  {m.email !== currentUser.email && (
                    <button style={styles.removeBtn} onClick={() => removeTeamMember(m.id)}>Remove</button>
                  )}
                  {m.email === currentUser.email && <span style={styles.youBadge}>You</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminWins() {
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('');

  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(collection(db, 'wins'));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setWins(all);
      setLoading(false);
    };
    load();
  }, []);

  // TCF admin moderation. Company admins deliberately cannot delete their
  // own people's wins, so the wall stays a safe place to post.
  const handleDeleteWin = async (win) => {
    const who = win.anonymous ? 'this anonymous win' : `${win.authorName}'s win`;
    if (!window.confirm(`Delete ${who}? This cannot be undone and it will drop out of the company's ROI totals.`)) return;
    try {
      await deleteDoc(doc(db, 'wins', win.id));
      setWins(prev => prev.filter(w => w.id !== win.id));
    } catch {
      alert('Could not delete that win. Please try again.');
    }
  };

  const companies = [...new Set(wins.map(w => w.company).filter(Boolean))];
  const filtered = filterCompany ? wins.filter(w => w.company === filterCompany) : wins;
  const roiWins = filtered.filter(w => w.annualizedHours > 0);
  const totalHours = Math.round(roiWins.reduce((a, w) => a + (w.annualizedHours || 0), 0));
  const catCounts = {};
  filtered.forEach(w => { if (w.category) catCounts[w.category] = (catCounts[w.category] || 0) + 1; });
  const topCats = Object.entries(catCounts).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div>
      <div style={styles.heading}>Wins Wall</div>
      <div style={styles.sub}>All wins across all companies.</div>
      <div style={{ marginBottom: '20px' }}>
        <select style={styles.select} value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
          <option value="">All Companies</option>
          {companies.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>
      {!loading && filtered.length > 0 && (
        <div style={{ background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '18px 24px', marginBottom: '20px', display: 'flex', gap: '32px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A' }}>{filtered.length}</div>
            <div style={{ fontSize: '11px', color: '#8A8A8A' }}>wins{filterCompany ? '' : ' across all companies'}</div>
          </div>
          <div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A' }}>{totalHours.toLocaleString()}</div>
            <div style={{ fontSize: '11px', color: '#8A8A8A' }}>hours / year saved</div>
          </div>
          {topCats.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: 'auto' }}>
              {topCats.map(([cat, n]) => <span key={cat} style={{ fontSize: '11px', fontWeight: '600', background: '#F2F0EC', color: '#1B2A4A', padding: '4px 10px' }}>{cat} · {n}</span>)}
            </div>
          )}
        </div>
      )}
      {loading ? <div style={{ color: '#3D3D3D' }}>Loading wins...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.length === 0 && <div style={{ color: '#3D3D3D', fontSize: '13px' }}>No wins posted yet.</div>}
          {filtered.map(win => (
            <div key={win.id} style={{ background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px' }}>
              <div style={{ fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '8px', fontWeight: '300' }}>{win.text}</div>
              {(win.annualizedHours > 0 || win.category || win.tool) && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {win.annualizedHours > 0 && <span style={{ fontSize: '11px', fontWeight: '600', background: '#1B2A4A', color: '#D4A847', padding: '4px 10px' }}>⏱ ~{win.annualizedHours} hrs/year saved</span>}
                  {win.category && <span style={{ fontSize: '11px', fontWeight: '600', background: '#F2F0EC', color: '#1B2A4A', padding: '4px 10px' }}>{win.category}</span>}
                  {win.tool && <span style={{ fontSize: '11px', fontWeight: '600', background: '#F2F0EC', color: '#1B2A4A', padding: '4px 10px' }}>{win.tool}</span>}
                </div>
              )}
              <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#3D3D3D', alignItems: 'center' }}>
                <span>{win.anonymous ? 'Anonymous' : win.authorName}</span>
                <span>{win.company}</span>
                <span>{win.createdAt?.toDate ? win.createdAt.toDate().toLocaleDateString() : ''}</span>
                <button
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, color: '#a32d2d', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' }}
                  onClick={() => handleDeleteWin(win)}
                >Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  nav: { background: '#1B2A4A', padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 },
  navLogo: { fontFamily: 'Playfair Display, serif', color: '#fff', fontSize: '16px', fontWeight: '700' },
  navUser: { fontSize: '12px', color: 'rgba(255,255,255,0.6)' },
  navLogout: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', background: 'none', border: '1px solid rgba(255,255,255,0.15)', padding: '5px 12px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  layout: { display: 'flex', minHeight: 'calc(100vh - 52px)' },
  sidebar: { width: '200px', background: '#243560', padding: '28px 0', flexShrink: 0 },
  sidebarItem: { padding: '11px 24px', fontSize: '13px', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '500', borderLeft: '3px solid transparent' },
  sidebarActive: { color: '#D4A847', borderLeftColor: '#D4A847', background: 'rgba(212,168,71,0.08)' },
  dot: { width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', opacity: 0.6, flexShrink: 0 },
  main: { flex: 1, padding: '36px 40px', overflowX: 'hidden' },
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#3D3D3D', marginBottom: '28px', fontWeight: '300' },
  backBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '16px', fontFamily: 'DM Sans, sans-serif', padding: 0 },
  statsStrip: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '12px', marginBottom: '20px' },
  statCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '16px 20px' },
  statLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#1B2A4A', marginBottom: '6px' },
  statVal: { fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: '900', color: '#1B2A4A' },
  statSub: { fontSize: '11px', color: '#3D3D3D', marginTop: '2px' },
  filterBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '16px 20px', display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' },
  journeyCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  journeyTitle: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#1B2A4A', marginBottom: '16px' },
  journeyRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' },
  journeyLabel: { width: '170px', fontSize: '12px', color: '#3D3D3D', flexShrink: 0 },
  journeyTrack: { flex: 1, background: '#F2F0EC', height: '24px', borderRadius: '3px', overflow: 'hidden' },
  journeyFill: { height: '100%', borderRadius: '3px', transition: 'width 0.5s ease-out' },
  journeyCount: { width: '78px', textAlign: 'right', fontSize: '14px', fontWeight: '700', color: '#1B2A4A', flexShrink: 0 },
  journeyPct: { fontSize: '11px', color: '#8A8A8A', fontWeight: '400' },
  select: { border: '1.5px solid #E8E6E2', padding: '8px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', minWidth: '130px' },
  searchInput: { border: '1.5px solid #E8E6E2', padding: '8px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FFFFFF', outline: 'none', flex: 1, minWidth: '150px' },
  exportBtn: { background: '#1B2A4A', color: '#FFFFFF', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginLeft: 'auto' },
  tableWrap: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: { textAlign: 'left', fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#1B2A4A', padding: '10px 12px', borderBottom: '2px solid #E8E6E2', background: '#F2F0EC', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #E8E6E2' },
  trSelected: { background: '#f0f4ff' },
  trAdmin: { background: '#fef9ec' },
  td: { padding: '10px 12px', color: '#3D3D3D', verticalAlign: 'middle' },
  emailCell: { fontSize: '11px', color: '#3D3D3D', fontWeight: '300', marginTop: '2px' },
  emailEditLink: { background: 'none', border: 'none', padding: 0, marginTop: '3px', color: '#185fa5', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' },
  editLinkRow: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' },
  editModalLabel: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '6px', display: 'block' },
  editModalField: { marginBottom: '14px' },
  emailModalOverlay: { position: 'fixed', inset: 0, background: 'rgba(27,42,74,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  emailModalCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', width: '420px', maxWidth: '100%' },
  emailModalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  emailModalSub: { fontSize: '12px', color: '#8A8A8A', marginBottom: '16px' },
  emailModalInput: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#3D3D3D', background: '#fff', outline: 'none', boxSizing: 'border-box' },
  emailModalNote: { fontSize: '12px', color: '#3D3D3D', marginTop: '12px', lineHeight: '1.5' },
  emailModalActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' },
  emailModalSave: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '9px 20px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  badge: { fontSize: '10px', fontWeight: '600', padding: '3px 8px', borderRadius: '2px', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  daysBadge: { fontSize: '11px', fontWeight: '600', background: '#e6f1fb', color: '#185fa5', padding: '2px 8px' },
  adminBadge: { fontSize: '10px', fontWeight: '600', background: '#fef5e4', color: '#854f0b', padding: '3px 8px' },
  makeAdminBtn: { background: '#1B2A4A', color: '#D4A847', border: 'none', padding: '4px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
  removeAdminBtn: { background: '#fff', color: '#C0392B', border: '1px solid #f5c6c6', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
  bulkPanel: { background: '#F2F0EC', border: '1.5px solid #E8E6E2', padding: '16px 20px', marginTop: '12px' },
  bulkLabel: { fontSize: '12px', fontWeight: '600', color: '#1B2A4A', marginBottom: '12px' },
  bulkActions: { display: 'flex', gap: '24px', flexWrap: 'wrap' },
  bulkGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  bulkGroupLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A' },
  smallSelect: { border: '1.5px solid #E8E6E2', padding: '6px 10px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#fff', outline: 'none' },
  actionBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '7px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  msgBox: { fontSize: '12px', padding: '8px 12px' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
  card: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  cardTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', fontWeight: '700', marginBottom: '12px' },
  dayTab: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: '500', color: '#3D3D3D' },
  dayTabActive: { background: '#1B2A4A', color: '#D4A847', borderColor: '#1B2A4A', fontWeight: '600' },
  inputField: { border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#fff', outline: 'none' },
  addBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 20px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  memberRow: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  memberName: { fontSize: '13px', fontWeight: '600', color: '#1B2A4A' },
  memberEmail: { fontSize: '11px', color: '#3D3D3D', marginTop: '2px' },
  removeBtn: { fontSize: '11px', color: '#C0392B', background: 'none', border: '1px solid #f5c6c6', padding: '4px 12px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  youBadge: { fontSize: '10px', fontWeight: '600', background: '#eaf3de', color: '#3b6d11', padding: '3px 10px' },
};

// ============================================================
// FINAL EXAM RESULTS ADMIN VIEW
// ============================================================

function ExamResultsAdmin({ user }) {
  const [results, setResults] = useState([]);
  const [users, setUsers] = useState({});
  const [engagements, setEngagements] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'passed' | 'failed' | 'overridden'
  const [companyFilter, setCompanyFilter] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [overrideTarget, setOverrideTarget] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [resultsSnap, usersSnap, engSnap] = await Promise.all([
          getDocs(collection(db, 'examResults')),
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'engagements')),
        ]);

        // Only show the "latest" docs (those where ID equals uid, not the per-attempt records)
        const latestResults = [];
        resultsSnap.forEach(d => {
          const data = d.data();
          if (data.isLatest === true) {
            latestResults.push({ id: d.id, ...data });
          }
        });

        // Sort by completedAt descending
        latestResults.sort((a, b) => {
          const aT = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : 0;
          const bT = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : 0;
          return bT - aT;
        });

        const usersMap = {};
        usersSnap.forEach(d => { usersMap[d.id] = d.data(); });

        const engMap = {};
        engSnap.forEach(d => { engMap[d.id] = d.data(); });

        setResults(latestResults);
        setUsers(usersMap);
        setEngagements(engMap);
      } catch (err) {
        console.error('Load exam results error:', err);
      }
      setLoading(false);
    };
    load();
  }, []);

  const reload = async () => {
    setLoading(true);
    const resultsSnap = await getDocs(collection(db, 'examResults'));
    const latestResults = [];
    resultsSnap.forEach(d => {
      const data = d.data();
      if (data.isLatest === true) latestResults.push({ id: d.id, ...data });
    });
    latestResults.sort((a, b) => {
      const aT = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : 0;
      const bT = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : 0;
      return bT - aT;
    });
    setResults(latestResults);
    setLoading(false);
  };

  // Apply filters
  const companies = [...new Set(results.map(r => r.companyCode).filter(Boolean))];
  let filtered = results;
  if (filter === 'passed') filtered = filtered.filter(r => r.passed);
  if (filter === 'failed') filtered = filtered.filter(r => !r.passed);
  if (filter === 'overridden') filtered = filtered.filter(r => r.overridden);
  if (companyFilter) filtered = filtered.filter(r => r.companyCode === companyFilter);

  const passCount = results.filter(r => r.passed).length;
  const failCount = results.filter(r => !r.passed).length;
  const overrideCount = results.filter(r => r.overridden).length;

  if (loading) {
    return <div style={styles.sub}>Loading exam results...</div>;
  }

  // Detail view modal
  if (selectedResult) {
    return <ExamResultDetailView
      result={selectedResult}
      userProfile={users[selectedResult.uid]}
      engagement={engagements[selectedResult.companyCode]}
      onClose={() => setSelectedResult(null)}
      onOverride={() => { setOverrideTarget(selectedResult); setSelectedResult(null); }}
      currentUser={user}
    />;
  }

  // Override modal
  if (overrideTarget) {
    return <OverrideExamModal
      target={overrideTarget}
      userProfile={users[overrideTarget.uid]}
      onCancel={() => setOverrideTarget(null)}
      onComplete={async () => {
        setOverrideTarget(null);
        await reload();
      }}
    />;
  }

  return (
    <div>
      <div style={styles.sub}>Final Exam results across every participant. Click a row for full detail and override options.</div>

      {/* Stats bar */}
      <div style={examStyles.statsRow}>
        <div style={examStyles.statCard}>
          <div style={examStyles.statNum}>{results.length}</div>
          <div style={examStyles.statLabel}>Total Attempts (Latest)</div>
        </div>
        <div style={{ ...examStyles.statCard, borderLeftColor: '#1a8a4a' }}>
          <div style={{ ...examStyles.statNum, color: '#1a8a4a' }}>{passCount}</div>
          <div style={examStyles.statLabel}>Passed</div>
        </div>
        <div style={{ ...examStyles.statCard, borderLeftColor: '#C0392B' }}>
          <div style={{ ...examStyles.statNum, color: '#C0392B' }}>{failCount}</div>
          <div style={examStyles.statLabel}>Failed</div>
        </div>
        <div style={{ ...examStyles.statCard, borderLeftColor: '#D4A847' }}>
          <div style={{ ...examStyles.statNum, color: '#D4A847' }}>{overrideCount}</div>
          <div style={examStyles.statLabel}>Overridden</div>
        </div>
      </div>

      {/* Filters */}
      <div style={examStyles.filterRow}>
        <div style={examStyles.filterGroup}>
          <span style={examStyles.filterLabel}>Status</span>
          {['all', 'passed', 'failed', 'overridden'].map(f => (
            <button
              key={f}
              style={{
                ...examStyles.filterBtn,
                ...(filter === f ? examStyles.filterBtnActive : {}),
              }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div style={examStyles.filterGroup}>
          <span style={examStyles.filterLabel}>Company</span>
          <select
            style={examStyles.companySelect}
            value={companyFilter}
            onChange={e => setCompanyFilter(e.target.value)}
          >
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c} value={c}>
                {engagements[c]?.company || c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results list */}
      {filtered.length === 0 ? (
        <div style={examStyles.emptyState}>
          {results.length === 0
            ? 'No exam attempts yet. Once participants start taking the exam, results will show up here.'
            : 'No results match the current filters.'}
        </div>
      ) : (
        <div style={examStyles.resultGrid}>
          {filtered.map(r => {
            const profile = users[r.uid] || {};
            const name = profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || r.userEmail || 'Unknown';
            const company = engagements[r.companyCode]?.company || r.companyCode || '—';
            const completedDate = r.completedAt?.toDate ? r.completedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

            return (
              <div key={r.id} style={examStyles.resultCard} onClick={() => setSelectedResult(r)}>
                <div style={examStyles.resultHeader}>
                  <div>
                    <div style={examStyles.resultName}>{name}</div>
                    <div style={examStyles.resultMeta}>{profile.jobTitle || '—'} · {company}</div>
                  </div>
                  <div style={{
                    ...examStyles.statusBadge,
                    background: r.passed ? '#eaf3de' : '#fdecea',
                    color: r.passed ? '#3b6d11' : '#a32d2d',
                  }}>
                    {r.overridden ? 'Overridden Pass' : r.passed ? 'Passed' : 'Failed'}
                  </div>
                </div>
                <div style={examStyles.resultBody}>
                  <div style={examStyles.scoreBlock}>
                    <div style={examStyles.scoreNum}>{r.totalScore || 0}</div>
                    <div style={examStyles.scoreOf}>/100</div>
                  </div>
                  <div style={examStyles.sectionScores}>
                    {['rice', 'workflow', 'roi', 'agent'].map(s => {
                      const score = r.sectionScores?.[s] || 0;
                      const failed = (r.failedSectionMins || []).includes(s);
                      return (
                        <div key={s} style={examStyles.sectionScoreMini}>
                          <div style={examStyles.sectionLabel}>{s}</div>
                          <div style={{
                            ...examStyles.sectionVal,
                            color: failed ? '#C0392B' : score >= 20 ? '#1a8a4a' : score >= 15 ? '#3D3D3D' : '#C0392B',
                          }}>
                            {score}<span style={examStyles.sectionDenom}>/25</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={examStyles.resultFooter}>
                  <span style={examStyles.resultDate}>Completed {completedDate}</span>
                  <span style={examStyles.viewLink}>View Details →</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// EXAM RESULT DETAIL VIEW
// Shows full answers, feedback, and override option for failures
// ============================================================

function ExamResultDetailView({ result, userProfile, engagement, onClose, onOverride, currentUser }) {
  const name = userProfile?.displayName || `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || result.userEmail || 'Unknown';
  const company = engagement?.company || result.companyCode || '—';
  const completed = result.completedAt?.toDate ? result.completedAt.toDate().toLocaleString() : '—';

  const sectionLabels = {
    rice: 'Role-Specific RICE Prompt',
    workflow: 'Workflow Redesign',
    roi: 'ROI Measurement',
    agent: 'Role-Specific Agent',
  };

  return (
    <div>
      <div style={examStyles.detailHeader}>
        <button style={examStyles.backBtn} onClick={onClose}>← Back to All Results</button>
      </div>

      <div style={examStyles.detailWrapper}>
        <div style={examStyles.detailTitleBar}>
          <div>
            <div style={examStyles.detailName}>{name}</div>
            <div style={examStyles.detailMeta}>{userProfile?.jobTitle || '—'} · {userProfile?.department || '—'} · {company}</div>
            <div style={examStyles.detailMeta}>Completed {completed} · Attempt #{result.attemptNumber || 1}</div>
          </div>
          <div style={examStyles.detailScoreBox}>
            <div style={{
              ...examStyles.detailScoreNum,
              color: result.passed ? '#1a8a4a' : '#C0392B',
            }}>{result.totalScore || 0}</div>
            <div style={examStyles.detailScoreLabel}>{result.passed ? (result.overridden ? 'OVERRIDDEN PASS' : 'PASSED') : 'FAILED'}</div>
          </div>
        </div>

        {result.overridden && (
          <div style={examStyles.overrideNotice}>
            <strong>This result was manually overridden.</strong> Original score: {result.totalScore !== result.originalScore ? `${result.originalScore || 'unknown'}.` : 'see audit log.'} Overridden by {result.overriddenBy || '—'}.
            {result.overrideReason && <div style={{ marginTop: '8px', fontStyle: 'italic' }}>Reason: {result.overrideReason}</div>}
          </div>
        )}

        {result.overallNote && (
          <div style={examStyles.overallNotePanel}>
            <div style={examStyles.overallNoteLabel}>Overall Feedback from Claude</div>
            <div style={examStyles.overallNoteText}>{result.overallNote}</div>
          </div>
        )}

        {/* Per-section breakdown */}
        {['rice', 'workflow', 'roi', 'agent'].map(secId => {
          const score = result.sectionScores?.[secId] || 0;
          const feedback = result.sectionFeedback?.[secId] || '';
          const answer1 = result.answers?.[secId]?.answer1 || '(no answer)';
          const answer2 = result.answers?.[secId]?.answer2;
          const failed = (result.failedSectionMins || []).includes(secId);

          return (
            <div key={secId} style={examStyles.detailSection}>
              <div style={examStyles.detailSectionHead}>
                <div style={examStyles.detailSectionLabel}>{sectionLabels[secId]}</div>
                <div style={{
                  ...examStyles.detailSectionScore,
                  background: failed ? '#C0392B' : score >= 20 ? '#1a8a4a' : '#D4A847',
                }}>
                  {score} / 25{failed ? ' · BELOW 15' : ''}
                </div>
              </div>

              <div style={examStyles.answerBlock}>
                <div style={examStyles.answerLabel}>First Answer</div>
                <div style={examStyles.answerText}>{answer1}</div>
              </div>

              {answer2 && (
                <div style={examStyles.answerBlock}>
                  <div style={examStyles.answerLabel}>Follow-up Answer</div>
                  <div style={examStyles.answerText}>{answer2}</div>
                </div>
              )}

              {feedback && (
                <div style={examStyles.feedbackBlock}>
                  <div style={examStyles.feedbackLabel}>Claude's Feedback</div>
                  <div style={examStyles.feedbackText}>{feedback}</div>
                </div>
              )}
            </div>
          );
        })}

        {/* Override button - only show for failed results that have not already been overridden */}
        {!result.passed && !result.overridden && (
          <div style={examStyles.overrideCTA}>
            <div style={examStyles.overrideCTATitle}>Manual Override</div>
            <div style={examStyles.overrideCTAText}>
              If you have reviewed this participant's work and believe they earned the certificate despite the failed exam, you can manually override the result. Every override is logged with your name and reason.
            </div>
            <button style={examStyles.overrideBtn} onClick={onOverride}>
              Override to Pass →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// OVERRIDE MODAL
// ============================================================

function OverrideExamModal({ target, userProfile, onCancel, onComplete }) {
  const [reason, setReason] = useState('');
  const [newScore, setNewScore] = useState(80);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const name = userProfile?.displayName || `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || target.userEmail || 'Unknown';

  const handleOverride = async () => {
    if (reason.trim().length < 50) {
      setError('Reason must be at least 50 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const functions = getFunctions(undefined, 'us-central1');
      const adminOverrideExam = httpsCallable(functions, 'adminOverrideExam');
      await adminOverrideExam({
        targetUid: target.uid,
        reason: reason.trim(),
        newScore: Number(newScore),
      });
      onComplete();
    } catch (err) {
      console.error('Override error:', err);
      setError(err.message || 'Could not complete override. Try again.');
    }
    setSubmitting(false);
  };

  return (
    <div>
      <div style={examStyles.detailHeader}>
        <button style={examStyles.backBtn} onClick={onCancel}>← Cancel Override</button>
      </div>

      <div style={examStyles.detailWrapper}>
        <div style={examStyles.overrideModalTitle}>Override Failed Exam</div>
        <div style={examStyles.overrideModalSub}>
          You are about to mark <strong>{name}</strong> as having passed the Final Exam. This will trigger their certificate and is logged in the audit trail with your name, the reason, and the original score.
        </div>

        <div style={examStyles.fieldGroup}>
          <label style={examStyles.fieldLabel}>New Score (out of 100)</label>
          <input
            type="number"
            min="80"
            max="100"
            style={examStyles.numberInput}
            value={newScore}
            onChange={e => setNewScore(e.target.value)}
          />
          <div style={examStyles.fieldHelp}>Minimum 80 to pass. Original score was {target.totalScore || 0}.</div>
        </div>

        <div style={examStyles.fieldGroup}>
          <label style={examStyles.fieldLabel}>Reason for Override (required, minimum 50 characters)</label>
          <textarea
            style={examStyles.textareaInput}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Example: Participant demonstrated all four competencies during live facilitation. Workbook clearly shows complete RICE prompts, full workflow redesign with time audit, working agent. Exam scoring missed the depth shown in the workbook."
            rows={5}
          />
          <div style={examStyles.fieldHelp}>{reason.length} / 50 characters minimum.</div>
        </div>

        {error && (
          <div style={examStyles.errorBox}>{error}</div>
        )}

        <div style={examStyles.overrideActions}>
          <button
            style={{ ...examStyles.confirmOverrideBtn, opacity: reason.trim().length >= 50 ? 1 : 0.4, cursor: reason.trim().length >= 50 ? 'pointer' : 'not-allowed' }}
            onClick={handleOverride}
            disabled={reason.trim().length < 50 || submitting}
          >
            {submitting ? 'Overriding...' : 'Confirm Override to Pass'}
          </button>
          <button style={examStyles.cancelOverrideBtn} onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// EXAM ADMIN STYLES
// ============================================================

const examStyles = {
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '24px' },
  statCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderLeft: '4px solid #1B2A4A', padding: '18px 22px' },
  statNum: { fontFamily: 'Playfair Display, serif', fontSize: '32px', fontWeight: '900', color: '#1B2A4A', lineHeight: '1' },
  statLabel: { fontSize: '11px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#8A8A8A', marginTop: '6px' },

  filterRow: { display: 'flex', gap: '24px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
  filterGroup: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  filterLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#8A8A8A' },
  filterBtn: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '7px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', cursor: 'pointer', fontWeight: '500' },
  filterBtnActive: { background: '#1B2A4A', color: '#D4A847', borderColor: '#1B2A4A', fontWeight: '600' },
  companySelect: { border: '1.5px solid #E8E6E2', padding: '7px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FFFFFF', outline: 'none', cursor: 'pointer' },

  emptyState: { background: '#F2F0EC', padding: '32px', textAlign: 'center', color: '#8A8A8A', fontStyle: 'italic', fontSize: '13px' },

  resultGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '14px' },
  resultCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '0', cursor: 'pointer', transition: 'border-color 0.15s' },
  resultHeader: { padding: '16px 20px 12px', borderBottom: '1px solid #E8E6E2', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  resultName: { fontFamily: 'Playfair Display, serif', fontSize: '15px', fontWeight: '700', color: '#1B2A4A', marginBottom: '3px' },
  resultMeta: { fontSize: '11px', color: '#8A8A8A' },
  statusBadge: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 10px', whiteSpace: 'nowrap' },

  resultBody: { padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '18px' },
  scoreBlock: { display: 'flex', alignItems: 'baseline', gap: '2px', minWidth: '80px' },
  scoreNum: { fontFamily: 'Playfair Display, serif', fontSize: '32px', fontWeight: '900', color: '#1B2A4A', lineHeight: '1' },
  scoreOf: { fontSize: '12px', color: '#8A8A8A' },

  sectionScores: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', flex: 1 },
  sectionScoreMini: { textAlign: 'center' },
  sectionLabel: { fontSize: '9px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '3px' },
  sectionVal: { fontFamily: 'Playfair Display, serif', fontSize: '16px', fontWeight: '700', lineHeight: '1' },
  sectionDenom: { fontSize: '10px', fontWeight: '400', color: '#8A8A8A' },

  resultFooter: { padding: '10px 20px', background: '#FAF8F5', borderTop: '1px solid #E8E6E2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  resultDate: { fontSize: '11px', color: '#8A8A8A' },
  viewLink: { fontSize: '11px', fontWeight: '600', color: '#1B2A4A' },

  // Detail view
  detailHeader: { marginBottom: '14px' },
  backBtn: { background: 'transparent', border: 'none', color: '#1B2A4A', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: '0', fontFamily: 'DM Sans, sans-serif' },

  detailWrapper: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '0' },
  detailTitleBar: { background: '#1B2A4A', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' },
  detailName: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '4px' },
  detailMeta: { fontSize: '12px', color: 'rgba(255,255,255,0.65)', marginBottom: '2px' },
  detailScoreBox: { background: 'rgba(212,168,71,0.15)', padding: '14px 20px', textAlign: 'center', minWidth: '120px' },
  detailScoreNum: { fontFamily: 'Playfair Display, serif', fontSize: '36px', fontWeight: '900', lineHeight: '1' },
  detailScoreLabel: { fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#D4A847', marginTop: '4px', fontWeight: '600' },

  overrideNotice: { background: '#FFF8E5', borderBottom: '1.5px solid #D4A847', padding: '14px 28px', fontSize: '13px', color: '#3D3D3D' },

  overallNotePanel: { background: '#F2F0EC', padding: '20px 28px', borderBottom: '1px solid #E8E6E2' },
  overallNoteLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '8px' },
  overallNoteText: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', fontStyle: 'italic' },

  detailSection: { padding: '20px 28px', borderBottom: '1px solid #E8E6E2' },
  detailSectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },
  detailSectionLabel: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', fontWeight: '700' },
  detailSectionScore: { color: '#fff', padding: '6px 14px', fontSize: '12px', fontWeight: '600', letterSpacing: '1px' },

  answerBlock: { marginBottom: '12px' },
  answerLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '6px' },
  answerText: { background: '#FAF8F5', padding: '12px 16px', fontSize: '13px', color: '#3D3D3D', lineHeight: '1.6', whiteSpace: 'pre-wrap', border: '1px solid #E8E6E2' },

  feedbackBlock: { background: '#1B2A4A', padding: '14px 18px', marginTop: '12px' },
  feedbackLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  feedbackText: { fontSize: '13px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.6', fontStyle: 'italic' },

  overrideCTA: { padding: '24px 28px', background: '#FAF8F5' },
  overrideCTATitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '8px' },
  overrideCTAText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '16px' },
  overrideBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '12px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },

  // Override modal
  overrideModalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', padding: '28px 32px 8px', background: '#1B2A4A', color: '#fff' },
  overrideModalSub: { padding: '16px 32px', background: '#FFF8E5', fontSize: '14px', color: '#3D3D3D', lineHeight: '1.6', borderBottom: '1px solid #D4A847' },

  fieldGroup: { padding: '20px 32px', borderBottom: '1px solid #E8E6E2' },
  fieldLabel: { display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', marginBottom: '8px' },
  numberInput: { width: '120px', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none' },
  textareaInput: { width: '100%', border: '1.5px solid #E8E6E2', padding: '12px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', boxSizing: 'border-box', resize: 'vertical', lineHeight: '1.6' },
  fieldHelp: { fontSize: '11px', color: '#8A8A8A', marginTop: '6px', fontStyle: 'italic' },

  errorBox: { padding: '12px 32px', background: '#fdecea', color: '#a32d2d', fontSize: '13px', borderBottom: '1px solid #E8E6E2' },

  overrideActions: { padding: '20px 32px', display: 'flex', gap: '12px' },
  confirmOverrideBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '13px 26px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700' },
  cancelOverrideBtn: { background: 'transparent', color: '#1B2A4A', border: '1.5px solid #1B2A4A', padding: '13px 26px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
